import {
  type CSSProperties,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';

import type {
  BuildingView,
  CodexEntryView,
  CodexFamilyView,
  DecodeMoveView,
  DiscoveryMarkerView,
  EnergyLinkView,
  KnowledgeWeaveView,
  PlayerVerticalSliceView,
  ResourceCode,
  ResourceDisplay,
  SettlementDistrictView,
  TribeRole,
  WorldCapabilityView,
  WorldRouteContractView,
  WeaveTargetView,
} from '@aztec-realms/shared-types';
import {
  GlyphIcon,
  JadeGlassPanel,
  LoadingState,
  Modal,
  OfflineState,
  ParchmentPanel,
  PrimaryActionButton,
  ProgressMeter,
  ResourceBar,
  RewardBundle,
  SecondaryActionButton,
  StatePanel,
  StonePanel,
} from '@aztec-realms/ui';

import {
  commandId,
  useVerticalSliceCommand,
  useVerticalSliceQuery,
} from './data/use-vertical-slice';
import {
  discoveriesNearPosition,
  cancelKnowledgeWeave,
  discoveryDistanceM,
  loadNearbyExplore,
  loadWeaveTargets,
  localExploreRefreshKey,
  mergeExploreDiscoveries,
  nearestDiscoveries,
  planKnowledgeWeave,
  startWorldRoute,
} from './data/nearby-explore-api';
import { visualFixtureName } from './data/vertical-slice';
import { useDailyDecodeEntryState } from './data/use-daily-decode-entry';
import { draftRotations, readDecodeDraft, saveDecodeDraft } from './data/decode-draft';
import { resolveTribeProjectLayer, resolveTribeProjectVisualForPhase } from './game/asset-manifest';
import { resolveConstructionStage } from './game/construction-stage';
import { ExploreMap } from './explore/ExploreMap';
import { useViewportExplore } from './explore/use-viewport-explore';
import { useSceneStartup } from './SceneStartup';
import { WorldNodeArt } from './explore/WorldNodeArt';
import { StopReport } from './explore/StopReport';
import { useExploreQuestClearance } from './explore/use-explore-quest-clearance';
import { WeaveProgress, WeaveRouteNotice } from './explore/explore-presentation';
import { useLocationCheck, type LocationCheckBudget } from './explore/use-location-check';
import './explore/explore-experience.css';
import type { JourneyMapPosition } from './explore/codex-map';
import {
  createForegroundPositionTracker,
  type ForegroundPositionTracker,
  mapPositionFromGeolocation,
  requestInitialForegroundPosition,
} from './foreground-geolocation';
import { DecodeCanvas } from './game/DecodeCanvas';
import type { DecodeVisualEvent } from './game/decode-renderer';
import { deriveDecodeVisualState } from './game/decode-visual-state';
import { SettlementCanvas } from './game/SettlementCanvas';
import { SettlementBuildPicker, type BuildPlacementReview } from './SettlementBuildPicker';
import { HomeBuildingArtwork } from './HomeBuildingArtwork';
import { PersonalProgress, SavePersonalGoalButton } from './PersonalProgress';
import {
  personalGoalLabel,
  personalGoalPath,
  personalGoalSummary,
  usePersonalRestoration,
} from './data/personal-progress-api';
import { missingBuildingResources, settlementPlayAction } from './settlement-goals';
import { SettlementPlacementControls } from './SettlementPlacementControls';
import { useSettlementPlacement } from './data/use-settlement-placement';
import './home-world.css';
import { DecodeCompletionPrompt } from './decode-completion-prompt';
import { decodeCompletionAction } from './decode-completion-action';
import { useJourneySafety } from './journey-safety';
import { useNetworkStatus } from './network-status';
import { usePresentationSettings } from './presentation-settings';
import { useVisualReady } from './visual-ready';
import { GuideCoachmark, GuideEntry, guidedLoopActive, guidedPath } from './OnboardingGuide';
import { TribeExchangePanel } from './TribeExchangePanel';
import { TribeAssistancePanel } from './TribeAssistancePanel';
import { CivicExpeditionPanel } from './CivicExpeditionPanel';
import { CivicExpeditionProvider } from './CivicExpeditionProvider';
import { CivicExpeditionCityStatus, CivicExpeditionScene } from './CivicExpeditionScene';
import { CodexGlyph } from './CodexGlyph';
import { CodexConstellations } from './CodexConstellations';
import {
  codexIllustration,
  codexProfileLabel,
  codexProfileOrder,
  codexRestored,
  codexTargetLabel,
} from './codex-presentation';

export { OnboardingScreen } from './OnboardingGuide';

function QueryState({
  pending,
  error,
  onRetry,
}: {
  pending: boolean;
  error: Error | null;
  onRetry: () => void;
}) {
  if (pending) {
    return (
      <section
        aria-live="polite"
        className="screen-state"
        data-kind="loading"
        data-visual-ready="true"
        data-visual-state="player-loading"
      >
        <LoadingState title="Restoring your view" />
      </section>
    );
  }
  if (error) {
    return (
      <section className="screen-state" data-kind="error">
        <StatePanel
          actionLabel="Try again"
          kind="error"
          onAction={onRetry}
          title="The path could not open"
        >
          {error.message}
        </StatePanel>
      </section>
    );
  }
  return null;
}

function BuildingInspector({
  building,
  district,
  capabilities,
  offline,
  pending,
  error,
  onUpgrade,
  onComplete,
  remainingLabel,
  selectedBranch,
  onBranchSelected,
  locked,
  onMove,
  balances,
  playAction,
  onPlay,
}: {
  building: BuildingView;
  district?: SettlementDistrictView;
  capabilities: readonly WorldCapabilityView[];
  offline: boolean;
  pending: boolean;
  error?: string;
  onUpgrade: (branchCode?: string) => void;
  onComplete?: () => void;
  remainingLabel?: string;
  selectedBranch?: string;
  onBranchSelected: (branchCode: string) => void;
  locked: boolean;
  onMove: () => void;
  balances: readonly ResourceDisplay[];
  playAction: { label: string; description: string };
  onPlay: () => void;
}) {
  const branchGroupId = useId();
  const canUpgrade =
    building.state === 'upgrade_available' &&
    (building.maxLevel === undefined || building.level < building.maxLevel);
  const specializationAvailable =
    building.specializationLevel !== undefined &&
    building.level + 1 >= building.specializationLevel;
  const specializationRequired =
    !building.branchCode && building.availableBranches.length > 0 && specializationAvailable;
  const missing = missingBuildingResources(building.upgradeCost, balances);
  const targetLevel =
    building.state === 'constructing'
      ? (building.constructionTargetLevel ?? building.level)
      : building.level + 1;
  const plannedCapabilities = selectedBranch
    ? (building.plannedBranchCapabilities?.[selectedBranch] ?? building.plannedCapabilities)
    : building.plannedCapabilities;

  return (
    <div className="building-inspector__content">
      <div className="building-inspector__reading">
        <div className="building-inspector__heading">
          <HomeBuildingArtwork code={building.type} level={building.level} variant="hero" />
          <div>
            <span className="realm-screen__eyebrow">
              Level {building.level} ·{' '}
              {building.state === 'constructing'
                ? 'Rising now'
                : canUpgrade
                  ? 'Ready to flourish'
                  : 'Established'}
            </span>
            <h2>{building.label}</h2>
          </div>
        </div>
        {(canUpgrade || building.state === 'constructing') && (
          <section className="building-goal" aria-label="Building goal">
            <strong>
              Your goal · {building.label}, level {targetLevel}
            </strong>
            <p>
              {plannedCapabilities?.length
                ? `At completion · World capabilities: ${plannedCapabilities.join(', ')}.`
                : 'At completion · The finished structure adds a completed level toward district expansion.'}
            </p>
          </section>
        )}
        <details className="building-inspector__details">
          <summary>District & world benefits</summary>
          {building.state !== 'constructing' && (
            <SecondaryActionButton disabled={offline || pending || locked} onClick={onMove}>
              Edit location
            </SecondaryActionButton>
          )}
          <p>{building.benefit}</p>
          {plannedCapabilities?.length ? (
            <small>
              Existing capabilities stay available. Higher levels do not promise extra material
              rewards.
            </small>
          ) : null}
          {district && (
            <p className="building-district-current">
              District · <strong>{district.displayName}</strong>
            </p>
          )}
          {building.branchCode && (
            <p className="building-branch-current">
              Completed specialization · <strong>{building.branchCode.replaceAll('_', ' ')}</strong>
            </p>
          )}
          {capabilities.length > 0 && (
            <section
              aria-label={`${building.label} world capabilities`}
              className="building-capabilities"
            >
              <h3>World capabilities</h3>
              <ul>
                {capabilities.map((capability) => (
                  <li data-capability-code={capability.code} key={capability.code}>
                    <strong>{capability.label}</strong>
                    <small>Effective level {capability.effectiveLevel}</small>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </details>
        {canUpgrade &&
          !building.branchCode &&
          building.availableBranches.length > 0 &&
          specializationAvailable && (
            <fieldset className="building-branches">
              <legend>Choose the next civic character</legend>
              {building.availableBranches.map((branch) => (
                <label key={branch.code}>
                  <input
                    checked={(selectedBranch ?? building.branchCode) === branch.code}
                    disabled={locked}
                    name={`${branchGroupId}-branch-${building.id}`}
                    onChange={() => onBranchSelected(branch.code)}
                    type="radio"
                  />
                  <span>
                    <strong>{branch.label}</strong>
                    <small>{branch.description}</small>
                  </span>
                </label>
              ))}
            </fieldset>
          )}
        {canUpgrade &&
          !building.branchCode &&
          building.availableBranches.length > 0 &&
          !specializationAvailable && (
            <p className="building-branch-locked">
              Civic specialization unlocks with level {building.specializationLevel}.
            </p>
          )}
      </div>
      <div className="building-inspector__action-band">
        {building.state === 'constructing' ? (
          <>
            <ProgressMeter
              label={`${building.label} construction`}
              value={(building.progress ?? 0) * 100}
            />
            {(building.progress ?? 0) < 1 && (
              <p className="building-inspector__duration">
                {remainingLabel && <strong>{remainingLabel} remaining. </strong>}
                Building in progress. Keep playing while it grows.
              </p>
            )}
            {(building.progress ?? 0) >= 1 && (
              <p role="status">Timer elapsed. Confirm completion to see the finished result.</p>
            )}
            {onComplete && (
              <PrimaryActionButton
                disabled={offline || pending || locked || (building.progress ?? 0) < 1}
                onClick={onComplete}
              >
                Complete construction
              </PrimaryActionButton>
            )}
          </>
        ) : canUpgrade ? (
          <>
            <ResourceBar resources={building.upgradeCost} />
            <p className="building-inspector__duration" role="status">
              {missing.length
                ? `Still needed: ${missing.map((item) => `${item.missing} ${item.label} (have ${item.have})`).join(' · ')}.`
                : 'Resources ready. Begin upgrade to spend them.'}
            </p>
            {building.upgradeDurationSeconds !== undefined && (
              <p className="building-inspector__duration">
                {building.upgradeDurationSeconds === 0
                  ? 'Ready immediately'
                  : building.upgradeDurationSeconds < 60
                    ? `${building.upgradeDurationSeconds} seconds`
                    : building.upgradeDurationSeconds < 3600
                      ? `${Math.ceil(building.upgradeDurationSeconds / 60)} minutes`
                      : `${Number((building.upgradeDurationSeconds / 3600).toFixed(1))} hours`}
                {' · '}Play while it builds.
              </p>
            )}
            {specializationRequired && !selectedBranch && (
              <small className="building-branch-prompt">
                Choose a civic character to continue.
              </small>
            )}
            <PrimaryActionButton
              disabled={
                offline ||
                pending ||
                locked ||
                missing.length > 0 ||
                (specializationRequired && !selectedBranch)
              }
              loading={pending}
              onClick={() =>
                onUpgrade(
                  specializationAvailable ? (selectedBranch ?? building.branchCode) : undefined,
                )
              }
            >
              Begin level {building.level + 1} upgrade
            </PrimaryActionButton>
          </>
        ) : (
          <p className="building-inspector__complete" role="status">
            {building.maxLevel !== undefined && building.level >= building.maxLevel
              ? `Maximum level ${building.maxLevel} reached. This structure is fully developed.`
              : 'This structure is established. No upgrade is currently available.'}
          </p>
        )}
        {(building.state === 'constructing' || missing.length > 0 || !canUpgrade) && (
          <section className="building-play-next" aria-label="Keep playing">
            <p>{playAction.description}</p>
            <SecondaryActionButton disabled={offline || pending || locked} onClick={onPlay}>
              {playAction.label}
            </SecondaryActionButton>
          </section>
        )}
        {offline && (
          <small>Read-only while offline. Your saved settlement remains available.</small>
        )}
        {error && (
          <span className="inline-error" role="alert">
            {error}
          </span>
        )}
      </div>
    </div>
  );
}

export function HomeExperience({ snapshot }: { snapshot: PlayerVerticalSliceView }) {
  const navigate = useNavigate();
  const location = useLocation();
  const settings = usePresentationSettings();
  const online = useNetworkStatus();
  const journey = useJourneySafety();
  const placement = useSettlementPlacement(journey.safety.mode === 'journey-passive');
  const command = useVerticalSliceCommand();
  const fixture = visualFixtureName();
  const fixtureParams = new URLSearchParams(location.search);
  const isMatureFixture = fixture === 'mature';
  const isUpgradePanelFixture = fixture === 'day-1' && fixtureParams.get('panel') === 'upgrade';
  const openSelectedStructure = isUpgradePanelFixture || fixtureParams.get('panel') === 'selected';
  const [selectedId, setSelectedId] = useState(
    fixtureParams.get('building') ?? snapshot.settlement.buildings[0]?.id,
  );
  const [selectedBranches, setSelectedBranches] = useState<Record<string, string>>({});
  const [sceneReady, setSceneReady] = useState(false);
  const [homePanel, setHomePanel] = useState<'buildings' | 'districts' | 'selected' | undefined>(
    openSelectedStructure || fixture === 'completed-specialization'
      ? 'selected'
      : fixture === 'district-expansion'
        ? 'districts'
        : undefined,
  );
  const [buildPickerOpen, setBuildPickerOpen] = useState(fixtureParams.get('panel') === 'build');
  const [buildReview, setBuildReview] = useState<BuildPlacementReview>();
  const [buildPickerMode, setBuildPickerMode] = useState<'build' | 'districts'>('build');
  const [editorOpen, setEditorOpen] = useState(false);
  const [overviewOpen, setOverviewOpen] = useState(false);
  const previousBuildings = useRef(snapshot.settlement.buildings);
  const [completedBuildingId, setCompletedBuildingId] = useState<string>();
  useEffect(() => {
    const completed = snapshot.settlement.buildings.find(
      (building) =>
        building.state !== 'constructing' &&
        previousBuildings.current.some(
          (previous) => previous.id === building.id && previous.state === 'constructing',
        ),
    );
    previousBuildings.current = snapshot.settlement.buildings;
    if (completed) queueMicrotask(() => setCompletedBuildingId(completed.id));
  }, [snapshot.settlement.buildings]);
  const playAction = settlementPlayAction(snapshot);
  const playNow = () => navigate(playAction.href);
  const editing = editorOpen && journey.safety.mode !== 'journey-passive';
  const reviewingBuild = buildPickerOpen && !!buildReview;
  const overviewVisible =
    overviewOpen && !editing && !placement.active && !homePanel && !buildPickerOpen;
  const reviewedSettlement = useMemo(
    () =>
      reviewingBuild && buildReview
        ? {
            ...snapshot.settlement,
            buildings: [...snapshot.settlement.buildings, buildReview.building],
          }
        : snapshot.settlement,
    [snapshot.settlement, reviewingBuild, buildReview],
  );
  const [editorMessage, setEditorMessage] = useState<string>();
  const [cameraCommand, setCameraCommand] = useState<{
    id: number;
    action: 'zoom-in' | 'zoom-out' | 'fit';
  }>();
  const editorToolsRef = useRef<HTMLDivElement>(null);
  const toolsRef = useRef<HTMLElement>(null);
  const wasPlacingRef = useRef(false);
  useEffect(() => {
    if (wasPlacingRef.current && !placement.active) {
      if (editing) editorToolsRef.current?.querySelector<HTMLSelectElement>('select')?.focus();
      else toolsRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
      queueMicrotask(() => setEditorMessage(undefined));
    }
    wasPlacingRef.current = placement.active;
  }, [placement.active, editing]);
  useEffect(() => {
    if (journey.safety.mode === 'journey-passive') queueMicrotask(() => setEditorOpen(false));
  }, [journey.safety.mode]);
  const homeTriggerRef = useRef<HTMLButtonElement | null>(null);
  const closeHomePanel = useCallback(() => {
    setHomePanel(undefined);
    // Canvas targets and list entries may no longer exist when the sheet closes.
    queueMicrotask(() => {
      const trigger = homeTriggerRef.current;
      if (trigger?.isConnected) trigger.focus();
      else toolsRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    });
  }, [setHomePanel]);
  const openBuildPicker = (mode: 'build' | 'districts' = 'build') => {
    setHomePanel(undefined);
    setBuildPickerMode(mode);
    setBuildPickerOpen(true);
  };
  const closeBuildPicker = useCallback(() => {
    setBuildPickerOpen(false);
    setBuildReview(undefined);
    queueMicrotask(() => {
      toolsRef.current
        ?.querySelector<HTMLButtonElement>(
          buildPickerMode === 'districts'
            ? '[aria-label="Open settlement districts"]'
            : '[aria-label="Build a new structure"]',
        )
        ?.focus();
    });
  }, [setBuildPickerOpen, buildPickerMode]);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('panel') === 'build') {
      queueMicrotask(() => {
        setHomePanel(undefined);
        setBuildPickerOpen(true);
      });
    } else if (params.get('panel') === 'selected') {
      queueMicrotask(() => {
        setBuildPickerOpen(false);
        setBuildReview(undefined);
        if (params.get('building')) setSelectedId(params.get('building')!);
        setHomePanel('selected');
      });
    }
  }, [location.search]);
  const constructionFixture = fixture?.match(/^construction-(0|25|50|75|100)$/);
  const visualState = isMatureFixture
    ? 'settlement-mature'
    : isUpgradePanelFixture
      ? 'building-upgrade-panel'
      : fixture === 'day-1'
        ? 'settlement-day-1'
        : constructionFixture
          ? `settlement-construction-${constructionFixture[1]}`
          : undefined;
  useVisualReady(visualState, sceneReady);
  const onBuildingSelected = useCallback(
    (buildingId: string) => {
      if (editing) {
        if (!online) {
          setEditorMessage('Reconnect to choose another building.');
          return;
        }
        if (placement.busy || placement.activeId === buildingId) return;
        if (placement.active && !placement.unchanged) {
          setEditorMessage('Save or cancel this move before choosing another building.');
          return;
        }
        const building = snapshot.settlement.buildings.find((item) => item.id === buildingId);
        if (!building || building.state === 'constructing') {
          setEditorMessage('Finish construction before moving this building.');
          return;
        }
        setEditorMessage(undefined);
        setSelectedId(buildingId);
        void placement.begin(buildingId);
        return;
      }
      setSelectedId(buildingId);
      setHomePanel('selected');
    },
    [
      editing,
      online,
      placement,
      snapshot.settlement.buildings,
      setEditorMessage,
      setSelectedId,
      setHomePanel,
    ],
  );
  const onSceneReady = useCallback(() => setSceneReady(true), [setSceneReady]);
  const onSceneUnavailable = useCallback(() => setSceneReady(false), [setSceneReady]);
  const selected =
    snapshot.settlement.buildings.find((building) => building.id === selectedId) ??
    snapshot.settlement.buildings[0];
  const construction = snapshot.settlement.construction.find(
    (item) => item.buildingId === selected?.id,
  );
  const selectedDistrict = snapshot.settlement.districts.find(
    (district) => district.code === selected?.districtCode,
  );
  const selectedCapabilities = snapshot.settlement.worldCapabilities.filter(
    (capability) => capability.sourceBuildingId === selected?.id,
  );
  const selectedBranch = selected ? selectedBranches[selected.id] : undefined;
  const guideActive = guidedLoopActive(location.search);
  const guidedBuildComplete =
    snapshot.settlement.activeMission.steps.find((step) => step.id === 'build')?.complete === true;
  const onBranchSelected = useCallback(
    (branchCode: string) => {
      if (!selected) return;
      setSelectedBranches((current) => ({ ...current, [selected.id]: branchCode }));
    },
    [selected, setSelectedBranches],
  );

  if (!selected) {
    return (
      <section className="screen-state">
        <StatePanel kind="empty" title="A clear lakeshore awaits">
          Your first structure will appear here after the guided restoration.
        </StatePanel>
      </section>
    );
  }

  return (
    <section
      className="player-screen home-screen"
      data-placement-mode={editing || placement.active || reviewingBuild ? 'true' : undefined}
      data-placement-active={placement.active ? 'true' : undefined}
      data-home-panel={homePanel ?? (buildPickerOpen ? 'build' : undefined)}
      data-build-review={reviewingBuild ? 'true' : undefined}
      data-editor-open={editing ? 'true' : undefined}
      data-home-overview={overviewVisible ? 'true' : undefined}
      data-guided={guideActive || undefined}
      data-upgrade-panel-open={homePanel === 'selected' ? 'true' : undefined}
      data-visual-ready={visualState && sceneReady ? 'true' : undefined}
      data-visual-state={visualState}
    >
      {guideActive && !editing && !placement.active && !homePanel && !buildPickerOpen && (
        <GuideCoachmark
          serverCompleted={snapshot.profile.onboardingCompleted}
          serverStep={snapshot.profile.onboardingStep}
          step="4 of 4 · Build"
          title={
            guidedBuildComplete ? 'Your change is part of the realm' : 'Choose one visible change'
          }
        >
          {guidedBuildComplete ? (
            <>
              <p>
                The server accepted the construction step. Return to the World to see what comes
                next.
              </p>
              <PrimaryActionButton onClick={() => navigate('/explore?guide=complete')}>
                Return to World
              </PrimaryActionButton>
            </>
          ) : (
            <p>
              Select a structure, review its real costs, then begin the upgrade. Construction and
              balances are verified by the server.
            </p>
          )}
        </GuideCoachmark>
      )}
      {(editing || placement.active || reviewingBuild) && (
        <h1 className="ar-visually-hidden">Arrange {snapshot.settlement.name}</h1>
      )}
      <header className="home-world-toolbar" hidden={editing || placement.active || reviewingBuild}>
        <div className="home-world-toolbar__identity">
          <span className="realm-screen__eyebrow">
            {isMatureFixture
              ? 'Day 90 · ceremonial bloom'
              : fixture
                ? 'Day 1 · first light'
                : 'Your settlement'}
          </span>
          <h1>{snapshot.settlement.name}</h1>
        </div>
        <button
          className="home-world-toolbar__overview"
          aria-label="Settlement overview"
          aria-pressed={overviewOpen}
          disabled={journey.safety.mode === 'journey-passive'}
          onClick={() => setOverviewOpen((current) => !current)}
          type="button"
        >
          {overviewOpen ? 'Close view' : 'Overview'}
        </button>
        <nav aria-label="Settlement tools" ref={toolsRef}>
          <button
            aria-label="Open settlement buildings"
            aria-haspopup="dialog"
            aria-expanded={homePanel === 'buildings'}
            disabled={journey.safety.mode === 'journey-passive'}
            onClick={(event) => {
              homeTriggerRef.current = event.currentTarget;
              setHomePanel('buildings');
            }}
            type="button"
          >
            <span aria-hidden="true">⌂</span>
            Buildings
          </button>
          <button
            aria-label="Build a new structure"
            aria-haspopup="dialog"
            aria-expanded={buildPickerOpen && buildPickerMode === 'build'}
            className="home-world-toolbar__build"
            disabled={journey.safety.mode === 'journey-passive'}
            onClick={() => openBuildPicker()}
            type="button"
          >
            <span aria-hidden="true">＋</span>
            Build
          </button>
          <button
            aria-label="Open settlement districts"
            aria-haspopup="dialog"
            aria-expanded={homePanel === 'districts'}
            disabled={journey.safety.mode === 'journey-passive'}
            onClick={(event) => {
              homeTriggerRef.current = event.currentTarget;
              setHomePanel('districts');
            }}
            type="button"
          >
            <span aria-hidden="true">◇</span>
            Districts
          </button>
          <button
            aria-label="Open settlement editor"
            disabled={!online || journey.safety.mode === 'journey-passive'}
            onClick={() => {
              setEditorMessage(undefined);
              setHomePanel(undefined);
              setEditorOpen(true);
              queueMicrotask(() => editorToolsRef.current?.querySelector('select')?.focus());
            }}
            type="button"
          >
            Editor
          </button>
        </nav>
      </header>
      {editing && (
        <div
          className="home-editor-tools"
          ref={editorToolsRef}
          aria-label="Settlement editor tools"
        >
          <select
            aria-label="Choose building to move"
            value={placement.activeId ?? ''}
            disabled={placement.busy || !online}
            onChange={(event) => onBuildingSelected(event.target.value)}
          >
            <option value="" disabled>
              Select building
            </option>
            {snapshot.settlement.buildings.map((building) => (
              <option
                key={building.id}
                value={building.id}
                disabled={building.state === 'constructing'}
              >
                {building.label} · Lv. {building.level}
                {building.state === 'constructing' ? ' · Building' : ''}
              </option>
            ))}
          </select>
          {(['zoom-out', 'zoom-in', 'fit'] as const).map((action) => (
            <button
              key={action}
              type="button"
              aria-label={
                action === 'fit'
                  ? 'Show whole district'
                  : action === 'zoom-in'
                    ? 'Zoom settlement in'
                    : 'Zoom settlement out'
              }
              onClick={() =>
                setCameraCommand((current) => ({ id: (current?.id ?? 0) + 1, action }))
              }
            >
              {action === 'fit' ? 'Fit' : action === 'zoom-in' ? '+' : '−'}
            </button>
          ))}
          <button
            type="button"
            disabled={placement.busy}
            onClick={() => {
              if (placement.active && !placement.unchanged) {
                setEditorMessage('Save or cancel your move before leaving the editor.');
                return;
              }
              placement.cancel();
              setEditorOpen(false);
              setEditorMessage(undefined);
              queueMicrotask(() =>
                toolsRef.current
                  ?.querySelector<HTMLButtonElement>('[aria-label="Open settlement editor"]')
                  ?.focus(),
              );
            }}
          >
            Exit
          </button>
        </div>
      )}
      {editing && !placement.active && (
        <div className="home-editor-hint" role="status">
          <strong>Arrange your settlement</strong>
          <p>Tap a building on the grid to move it. Drag the ground to look around.</p>
        </div>
      )}
      {buildPickerOpen && (
        <SettlementBuildPicker
          onReviewChange={setBuildReview}
          onManageExisting={(buildingId) => {
            setBuildPickerOpen(false);
            setBuildReview(undefined);
            onBuildingSelected(buildingId);
          }}
          onCamera={(action) =>
            setCameraCommand((current) => ({ id: (current?.id ?? 0) + 1, action }))
          }
          goalOwner={snapshot.source === 'api' ? snapshot.profile.userId : undefined}
          initialCode={fixtureParams.get('goal') ?? undefined}
          playAction={playAction}
          onPlay={playNow}
          mode={buildPickerMode}
          offline={!online}
          locked={journey.safety.mode === 'journey-passive'}
          onClose={closeBuildPicker}
          onCreated={(buildingId) => {
            setBuildPickerOpen(false);
            setBuildReview(undefined);
            onBuildingSelected(buildingId);
          }}
        />
      )}
      {!online && (
        <div className="world-state-banner">
          <OfflineState />
        </div>
      )}
      {snapshot.source === 'api' &&
        !editing &&
        !placement.active &&
        !homePanel &&
        !buildPickerOpen &&
        !guideActive && (
          <PersonalProgress key={snapshot.profile.userId} snapshot={snapshot} surface="home" />
        )}
      <SettlementCanvas
        editing={editing || reviewingBuild}
        cameraCommand={cameraCommand}
        interactive={
          journey.safety.mode !== 'journey-passive' &&
          !homePanel &&
          (!buildPickerOpen || (reviewingBuild && !buildReview?.busy)) &&
          !placement.busy
        }
        placement={
          reviewingBuild
            ? buildReview?.placement
            : placement.preview
              ? { ...placement.preview, unobscuredViewport: true }
              : undefined
        }
        onPlacementChanged={reviewingBuild ? buildReview?.move : placement.move}
        onBuildingSelected={reviewingBuild ? () => undefined : onBuildingSelected}
        onReady={onSceneReady}
        onUnavailable={onSceneUnavailable}
        qualityTier={settings.qualityTier}
        reducedMotion={settings.reducedMotion}
        selectedBuildingId={
          reviewingBuild
            ? buildReview?.building.id
            : (placement.activeId ??
              (editing || homePanel === 'selected' ? selected.id : undefined))
        }
        snapshot={reviewedSettlement}
      />
      {overviewVisible && (
        <div className="home-overview-companion">
          <p>Main island overview · Drag to visit your other districts.</p>
          <div role="group" aria-label="Buildings in your settlement">
            {snapshot.settlement.buildings.map((building) => (
              <button
                key={building.id}
                type="button"
                disabled={journey.safety.mode === 'journey-passive'}
                onClick={(event) => {
                  homeTriggerRef.current = event.currentTarget;
                  onBuildingSelected(building.id);
                }}
              >
                <span>{building.label}</span>
                <small>
                  Lv. {building.level}
                  {building.state === 'constructing' ? ' · Building' : ''}
                </small>
              </button>
            ))}
          </div>
        </div>
      )}
      {placement.active && (
        <SettlementPlacementControls editor={placement} label={selected.label} offline={!online} />
      )}
      {(placement.notice || (editing && editorMessage)) && (
        <p className="home-placement-notice" role="status">
          {editing && editorMessage ? editorMessage : placement.notice}
        </p>
      )}
      {homePanel &&
        createPortal(
          <div className="home-settlement-overlay" data-home-panel={homePanel}>
            <Modal
              key={homePanel}
              open
              title={
                homePanel === 'buildings'
                  ? 'Settlement buildings'
                  : homePanel === 'districts'
                    ? 'Settlement districts'
                    : 'Selected structure'
              }
              onClose={closeHomePanel}
            >
              <div className="home-settlement-overlay__body">
                {homePanel === 'buildings' && (
                  <>
                    <p className="home-settlement-overlay__intro">
                      Choose a building to see its benefits, progress and available upgrades. You
                      can also tap it in your settlement.
                    </p>
                    <div
                      className="building-accessibility"
                      role="group"
                      aria-label="Settlement structures"
                    >
                      {snapshot.settlement.buildings.map((building) => (
                        <button
                          aria-pressed={building.id === selected.id}
                          className="building-chip"
                          data-building-id={building.id}
                          data-building-code={building.type}
                          data-construction-stage={
                            building.state === 'constructing'
                              ? resolveConstructionStage(building.progress ?? 0).stage
                              : undefined
                          }
                          data-level={building.level}
                          data-state={building.state}
                          data-testid="settlement-building"
                          disabled={journey.safety.mode === 'journey-passive'}
                          key={building.id}
                          onClick={() => onBuildingSelected(building.id)}
                          type="button"
                        >
                          <HomeBuildingArtwork code={building.type} level={building.level} />
                          <span className="building-chip__details">
                            <span className="building-chip__title">
                              {building.label}
                              <span aria-hidden="true">↗</span>
                            </span>
                            <small>
                              Level {building.level} ·{' '}
                              {building.state === 'constructing'
                                ? 'Under construction'
                                : building.maxLevel !== undefined &&
                                    building.level >= building.maxLevel
                                  ? 'Fully developed'
                                  : building.state === 'upgrade_available'
                                    ? 'Upgrade available'
                                    : 'Established'}
                            </small>
                          </span>
                        </button>
                      ))}
                    </div>
                    <PrimaryActionButton
                      disabled={journey.safety.mode === 'journey-passive'}
                      onClick={() => openBuildPicker()}
                    >
                      Build something new
                    </PrimaryActionButton>
                  </>
                )}
                {homePanel === 'districts' && (
                  <>
                    <section aria-label="Settlement districts" className="settlement-depth">
                      <div className="settlement-districts">
                        <p className="home-settlement-overlay__intro">
                          Grow beyond the lakeshore. Each district adds space for a new chapter of
                          your city.
                        </p>
                        <ul>
                          {snapshot.settlement.districts.map((district) => (
                            <li
                              data-district-code={district.code}
                              data-state={district.unlocked ? 'unlocked' : 'locked'}
                              key={district.code}
                            >
                              <span aria-hidden="true">{district.unlocked ? '◇' : '⌁'}</span>
                              <span>
                                <strong>{district.displayName}</strong>
                                <small>{district.unlocked ? 'Unlocked' : 'Locked'}</small>
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <details className="settlement-history">
                        <summary>
                          City chronicle · {snapshot.settlement.history.length}{' '}
                          {snapshot.settlement.history.length === 1 ? 'milestone' : 'milestones'}
                        </summary>
                        <ol>
                          {snapshot.settlement.history.slice(0, 5).map((event) => (
                            <li key={event.id}>
                              <strong>{event.eventType.replaceAll('_', ' ')}</strong>
                              <small>
                                {event.districtCode.replaceAll('_', ' ')}
                                {event.specializationCode
                                  ? ` · ${event.specializationCode.replaceAll('_', ' ')}`
                                  : ''}
                              </small>
                            </li>
                          ))}
                        </ol>
                      </details>
                    </section>
                    <PrimaryActionButton
                      disabled={journey.safety.mode === 'journey-passive'}
                      onClick={() => openBuildPicker('districts')}
                    >
                      Plan district expansion
                    </PrimaryActionButton>
                  </>
                )}
                {homePanel === 'selected' && (
                  <div className="building-inspector">
                    {completedBuildingId === selected.id ? (
                      <section
                        className="building-goal"
                        aria-label="Construction result"
                        role="status"
                      >
                        <HomeBuildingArtwork
                          code={selected.type}
                          level={selected.level}
                          variant="hero"
                        />
                        <h2>
                          {selected.label} · level {selected.level} complete
                        </h2>
                        <p>Your finished structure is now part of {snapshot.settlement.name}.</p>
                        {selected.branchCode && (
                          <p>Specialization: {selected.branchCode.replaceAll('_', ' ')}.</p>
                        )}
                        <p>
                          {selectedCapabilities.length
                            ? `Active now: ${selectedCapabilities.map((item) => item.label).join(', ')}.`
                            : 'This completed level now counts toward district expansion.'}
                        </p>
                        <div className="home-inspector-actions" aria-label="Choose what to do next">
                          <PrimaryActionButton
                            onClick={() => {
                              setCompletedBuildingId(undefined);
                              closeHomePanel();
                            }}
                          >
                            See it in your city
                          </PrimaryActionButton>
                          <SecondaryActionButton
                            disabled={journey.safety.mode === 'journey-passive'}
                            onClick={() => {
                              setCompletedBuildingId(undefined);
                              openBuildPicker();
                            }}
                          >
                            Choose your next building
                          </SecondaryActionButton>
                          <SecondaryActionButton
                            disabled={!online || journey.safety.mode === 'journey-passive'}
                            onClick={playNow}
                          >
                            {playAction.label}
                          </SecondaryActionButton>
                        </div>
                      </section>
                    ) : (
                      <>
                        <BuildingInspector
                          balances={snapshot.settlement.resources}
                          playAction={playAction}
                          onPlay={playNow}
                          onMove={() => {
                            setHomePanel(undefined);
                            setEditorOpen(true);
                            setEditorMessage(undefined);
                            void placement.begin(selected.id);
                          }}
                          building={selected}
                          capabilities={selectedCapabilities}
                          district={selectedDistrict}
                          error={command.error?.message}
                          offline={!online}
                          onComplete={
                            construction
                              ? () =>
                                  command.mutate({
                                    type: 'settlement.complete',
                                    commandId: commandId('settlement.complete'),
                                    constructionId: construction.id,
                                  })
                              : undefined
                          }
                          onUpgrade={(branchCode) =>
                            command.mutate({
                              type: 'settlement.upgrade',
                              commandId: commandId('settlement.upgrade'),
                              buildingId: selected.id,
                              branchCode,
                            })
                          }
                          locked={journey.safety.mode === 'journey-passive'}
                          onBranchSelected={onBranchSelected}
                          pending={command.isPending}
                          selectedBranch={selectedBranch}
                          remainingLabel={construction?.remainingLabel}
                        />
                        <div className="home-inspector-actions">
                          {snapshot.source === 'api' &&
                            (selected.state === 'constructing' ||
                              selected.level < (selected.maxLevel ?? 5)) && (
                              <SavePersonalGoalButton
                                key={selected.id}
                                owner={snapshot.profile.userId!}
                                buildingCode={selected.type}
                                buildingId={selected.id}
                                disabled={
                                  !online ||
                                  journey.safety.mode === 'journey-passive' ||
                                  command.isPending
                                }
                              />
                            )}
                          <SecondaryActionButton
                            disabled={journey.safety.mode === 'journey-passive'}
                            onClick={() => setHomePanel('buildings')}
                          >
                            All buildings
                          </SecondaryActionButton>
                          <SecondaryActionButton
                            disabled={journey.safety.mode === 'journey-passive'}
                            onClick={() => openBuildPicker()}
                          >
                            Build something new
                          </SecondaryActionButton>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </Modal>
          </div>,
          document.querySelector('.ar-game-shell') ?? document.body,
        )}
    </section>
  );
}

export function HomeScreen() {
  const query = useVerticalSliceQuery({ refreshConstruction: true });
  const state = (
    <QueryState
      error={query.error}
      onRetry={() => void query.refetch()}
      pending={query.isPending}
    />
  );
  return query.data ? <HomeExperience snapshot={query.data} /> : state;
}

function discoveryStatus(discovery: DiscoveryMarkerView, interactionRadiusM: number) {
  return discovery.state === 'eligible'
    ? 'Ready to play'
    : discovery.state === 'queued'
      ? `Seen · reach within ${Math.round(interactionRadiusM)} m to unlock`
      : discovery.state === 'claimed'
        ? 'Challenge saved'
        : discovery.state === 'cooldown'
          ? 'Resting · try another stop'
          : `Travel within ${Math.round(interactionRadiusM)} m to play`;
}

function discoveryGlyph(kind: DiscoveryMarkerView['kind']) {
  if (kind === 'glyph_fragment') return '✦';
  if (kind === 'celestial') return '☼';
  if (kind === 'ruin') return '⌂';
  if (kind === 'cache') return '◇';
  if (kind === 'tribe') return '⚑';
  return '❋';
}

function worldNodeLabel(kind: DiscoveryMarkerView['nodeKind']): string | undefined {
  if (!kind) return undefined;
  return {
    glyph_archive: 'Glyph Archive',
    civic_nexus: 'Civic Nexus',
    verdant_waypoint: 'Verdant Waypoint',
    water_gate: 'Water Gate',
    celestial_observatory: 'Celestial Observatory',
    market_exchange: 'Market Exchange',
  }[kind];
}

function WalkingIcon() {
  return (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 32 32">
      <circle cx="18" cy="6" r="3" />
      <path d="m16 11-4 7 5 3 2-7 4 4 4-2-6-6Zm1 10-5 8m7-7 6 7" />
    </svg>
  );
}

function NearbyIcon() {
  return (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 32 32">
      <circle cx="16" cy="14" r="5" />
      <path d="M16 2v4m0 16v8M4 14h4m16 0h4M7.5 5.5l3 3m11-3-3 3" />
      <path d="m11 23 5 6 5-6" />
    </svg>
  );
}

function CodexExpeditionIcon() {
  return (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 32 32">
      <circle cx="16" cy="16" r="6" />
      <path d="M16 3v4m0 18v4M3 16h4m18 0h4M6.8 6.8l2.8 2.8m12.8 12.8 2.8 2.8m0-18.4-2.8 2.8M9.6 22.4l-2.8 2.8" />
      <path d="m13.2 16 2 2.1 4-4.4" />
    </svg>
  );
}

function RouteChainIcon() {
  return (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 32 32">
      <circle cx="7" cy="23" r="3" />
      <circle cx="16" cy="10" r="3" />
      <circle cx="25" cy="20" r="3" />
      <path d="m9 20 5-7m5-1 4 5" />
    </svg>
  );
}

export function ExploreExperience({ snapshot }: { snapshot: PlayerVerticalSliceView }) {
  const exploreLayoutRef = useRef<HTMLElement>(null);
  useExploreQuestClearance(exploreLayoutRef);
  const navigate = useNavigate();
  const location = useLocation();
  const settings = usePresentationSettings();
  const online = useNetworkStatus();
  const journey = useJourneySafety();
  const stopReportSafetyLocked = journey.safety.mode === 'journey-passive';
  const command = useVerticalSliceCommand();
  const [browserLocation, setBrowserLocation] = useState<{
    error?: string;
    phase: 'idle' | 'locating' | 'located' | 'error';
    position?: JourneyMapPosition;
  }>({ phase: 'idle' });
  const [nearbyDiscoveries, setNearbyDiscoveries] = useState<readonly DiscoveryMarkerView[]>([]);
  const [nearbyEnergyLinks, setNearbyEnergyLinks] = useState<readonly EnergyLinkView[]>([]);
  const [nearbyWeaves, setNearbyWeaves] = useState<readonly KnowledgeWeaveView[]>([]);
  const [nearbyRoutes, setNearbyRoutes] = useState<readonly WorldRouteContractView[]>([]);
  const [nearbyError, setNearbyError] = useState<string>();
  const [routeError, setRouteError] = useState<string>();
  const [routePendingId, setRoutePendingId] = useState<string>();
  const [requestedWeaveOriginId, setWeaveOriginId] = useState<string>();
  const [requestedWeaveTargetId, setWeaveTargetId] = useState<string>();
  const [weaveTargets, setWeaveTargets] = useState<readonly WeaveTargetView[]>([]);
  const [weavePending, setWeavePending] = useState(false);
  const [weaveError, setWeaveError] = useState<string>();
  const weavePanelRef = useRef<HTMLElement>(null);
  const { beginJourney, verifyCurrentPosition } = journey;
  const latestJourney = useRef(journey);
  const exploreMounted = useRef(true);
  useEffect(() => {
    exploreMounted.current = true;
    return () => {
      exploreMounted.current = false;
    };
  }, []);
  useEffect(() => {
    latestJourney.current = journey;
  }, [journey]);
  const autoJourneyAttempted = useRef(false);
  const locationCheckBudget = useRef<LocationCheckBudget>({ lastAt: -Infinity, active: false });
  const weavePlanInFlight = useRef(false);
  const weaveTargetsGeneration = useRef(0);
  const invalidateWeaveTargets = useCallback(() => {
    weaveTargetsGeneration.current += 1;
    if (!weavePlanInFlight.current) setWeavePending(false);
  }, []);
  useEffect(() => {
    if (!journey.foregroundVisible) invalidateWeaveTargets();
    return () => {
      weaveTargetsGeneration.current += 1;
    };
  }, [journey.foregroundVisible, invalidateWeaveTargets]);
  const [localWorldCenter, setLocalWorldCenter] = useState<readonly [number, number] | undefined>(
    undefined,
  );
  const presentationTrackerRef = useRef<ForegroundPositionTracker | null>(null);
  const nearbyRefreshKey = useRef<string | undefined>(undefined);
  const nearbyRefreshSequence = useRef(0);
  const stationaryNearbyRefreshCompleted = useRef(false);
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState<string>();
  const onMapUnavailable = useCallback(
    () => setMapError('Your street map could not load. Try again to open nearby places.'),
    [],
  );
  const [mobilePanel, setMobilePanel] = useState<
    'journey' | 'nearby' | 'routes' | 'weaves' | undefined
  >(new URLSearchParams(location.search).get('panel') === 'weaves' ? 'weaves' : undefined);
  const onMapReady = useCallback(() => setMapReady(true), []);
  const fixture = visualFixtureName();
  const visualState =
    fixture === 'new-discovery'
      ? 'explore-new-discovery'
      : fixture === 'passive'
        ? 'explore-passive'
        : undefined;
  // A fresh Weave walk temporarily clears its proof samples, not the player's
  // presentation location. Keep the map mounted while the new GPS fix arrives;
  // this retained marker never supplies server eligibility or rewards.
  const displayedPosition = journey.active
    ? (journey.currentPosition ?? browserLocation.position)
    : browserLocation.position;
  const viewportWorld = useViewportExplore(
    displayedPosition,
    !fixture && online && journey.foregroundVisible && journey.safety.mode !== 'journey-passive',
  );
  useEffect(() => {
    const position = journey.currentPosition;
    if (!journey.active || !position) return;
    let cancelled = false;
    queueMicrotask(() => {
      if (!cancelled)
        setBrowserLocation((current) =>
          current.position === position ? current : { phase: 'located', position },
        );
    });
    return () => {
      cancelled = true;
    };
  }, [journey.active, journey.currentPosition]);
  const deterministicJourneyFixture = Boolean(
    (window as Window & { __AZR_E2E__?: { deterministic?: boolean } }).__AZR_E2E__?.deterministic &&
    new URLSearchParams(location.search).get('fixture') === 'scenario-a',
  );
  const requiresCurrentLocation =
    !fixture &&
    !deterministicJourneyFixture &&
    typeof navigator !== 'undefined' &&
    !navigator.userAgent.toLowerCase().includes('jsdom');
  useEffect(() => {
    if (!journey.foregroundVisible) {
      autoJourneyAttempted.current = false;
      return;
    }
    if (journey.active && journey.phase === 'recording') {
      autoJourneyAttempted.current = true;
      return;
    }
    if (
      (!requiresCurrentLocation && !deterministicJourneyFixture) ||
      !online ||
      autoJourneyAttempted.current ||
      (journey.active && journey.phase !== 'recovery') ||
      (journey.phase !== 'idle' && journey.phase !== 'recovery')
    )
      return;
    autoJourneyAttempted.current = true;
    void beginJourney();
  }, [
    requiresCurrentLocation,
    deterministicJourneyFixture,
    online,
    journey.foregroundVisible,
    journey.active,
    journey.phase,
    beginJourney,
  ]);
  const liveGpsActive =
    journey.active || (requiresCurrentLocation && browserLocation.phase === 'located');
  const waitingForCurrentLocation = requiresCurrentLocation && !displayedPosition;
  const initialWorldReady = !requiresCurrentLocation || Boolean(localWorldCenter);
  useSceneStartup(
    waitingForCurrentLocation ? 'location' : !initialWorldReady ? 'world' : 'map',
    initialWorldReady && mapReady,
    browserLocation.phase === 'error'
      ? 'Allow location in your browser to see your nearby streets, then try again.'
      : nearbyError && !initialWorldReady
        ? 'Nearby places could not load. Try again to continue exploring.'
        : mapError,
  );
  const localWorldActive = Boolean(displayedPosition || nearbyDiscoveries.length > 0);
  const discoveries = useMemo(
    () =>
      waitingForCurrentLocation
        ? []
        : localWorldActive
          ? mergeExploreDiscoveries(
              localWorldCenter
                ? discoveriesNearPosition(snapshot.explore.discoveries, localWorldCenter).filter(
                    (discovery) =>
                      discovery.state === 'eligible' ||
                      discovery.state === 'queued' ||
                      discovery.state === 'claimed' ||
                      discovery.state === 'cooldown',
                  )
                : [],
              nearbyDiscoveries,
              journey.queuedDiscoveries,
            )
          : snapshot.explore.discoveries,
    [
      journey.queuedDiscoveries,
      localWorldActive,
      localWorldCenter,
      nearbyDiscoveries,
      snapshot.explore.discoveries,
      waitingForCurrentLocation,
    ],
  );
  const exploreSnapshot = useMemo(
    () => ({
      ...snapshot.explore,
      center: displayedPosition?.coordinates ?? localWorldCenter ?? snapshot.explore.center,
      discoveries: mergeExploreDiscoveries(
        viewportWorld.query.data?.discoveries ?? [],
        discoveries,
      ),
      energyLinks: localWorldActive ? nearbyEnergyLinks : snapshot.explore.energyLinks,
      weaves: localWorldActive ? nearbyWeaves : snapshot.explore.weaves,
      routeContracts: localWorldActive ? nearbyRoutes : snapshot.explore.routeContracts,
      regionName: localWorldActive ? 'Living World' : snapshot.explore.regionName,
    }),
    [
      discoveries,
      displayedPosition?.coordinates,
      localWorldActive,
      localWorldCenter,
      nearbyEnergyLinks,
      nearbyRoutes,
      nearbyWeaves,
      snapshot.explore,
      viewportWorld.query.data,
    ],
  );
  const revealDiscovery =
    fixture === 'new-discovery'
      ? exploreSnapshot.discoveries.find((item) => item.state === 'eligible')
      : undefined;
  useVisualReady(visualState, mapReady);
  const [requestedSelectedId, setSelectedId] = useState<string>();
  const [selectedDetailsOpen, setSelectedDetailsOpen] = useState(fixture === 'new-discovery');
  const closeStopDetails = useCallback(() => {
    invalidateWeaveTargets();
    setSelectedDetailsOpen(false);
  }, [invalidateWeaveTargets]);
  const selectedId =
    requestedSelectedId &&
    exploreSnapshot.discoveries.some((discovery) => discovery.id === requestedSelectedId)
      ? requestedSelectedId
      : (discoveries.find((discovery) => discovery.state === 'eligible')?.id ?? discoveries[0]?.id);
  const selected = exploreSnapshot.discoveries.find((item) => item.id === selectedId);
  const selectedDistanceM = selected ? discoveryDistanceM(selected, displayedPosition) : undefined;
  const nearestListDiscoveries = useMemo(
    () => nearestDiscoveries(discoveries, displayedPosition, 10),
    [displayedPosition, discoveries],
  );
  // An unselected origin must not match an ordinary fragment's missing worldNodeId.
  // Resolve all selection controls from the current snapshot so a vanished origin
  // cannot leave the player stuck with stale destination actions.
  const weaveOrigin = requestedWeaveOriginId
    ? exploreSnapshot.discoveries.find((item) => item.worldNodeId === requestedWeaveOriginId)
    : undefined;
  const weaveOriginId = weaveOrigin?.worldNodeId;
  const selectedWeaveTarget = weaveTargets.find((item) => item.nodeId === requestedWeaveTargetId);
  const activeWeavePlan = exploreSnapshot.weaves?.find((weave) => weave.status === 'planned');
  const weaveWalkStarted = Boolean(
    activeWeavePlan &&
    journey.active &&
    journey.startedAt &&
    Date.parse(journey.startedAt) >= Date.parse(activeWeavePlan.plannedAt),
  );
  const onDiscoverySelected = useCallback(
    (discoveryId: string) => {
      invalidateWeaveTargets();
      const discovery = exploreSnapshot.discoveries.find((item) => item.id === discoveryId);
      setSelectedId(discoveryId);
      if (weaveOriginId && weaveTargets.length > 0) {
        setWeaveTargetId(
          weaveTargets.find((target) => target.nodeId === discovery?.worldNodeId)?.nodeId,
        );
        setSelectedDetailsOpen(false);
        setMobilePanel('weaves');
        setWeaveError(
          weaveTargets.some((target) => target.nodeId === discovery?.worldNodeId)
            ? undefined
            : 'This stop is not available for this connection. Choose a highlighted destination.',
        );
        return;
      }
      if (
        activeWeavePlan &&
        (discovery?.worldNodeId === activeWeavePlan.sourceNodeId ||
          discovery?.worldNodeId === activeWeavePlan.destinationNodeId)
      ) {
        setSelectedDetailsOpen(false);
        setMobilePanel(undefined);
        return;
      }
      setSelectedDetailsOpen(true);
      setMobilePanel(undefined);
    },
    [
      activeWeavePlan,
      exploreSnapshot.discoveries,
      weaveOriginId,
      weaveTargets,
      invalidateWeaveTargets,
    ],
  );
  useEffect(() => {
    const panel = weavePanelRef.current;
    if (mobilePanel !== 'weaves' || !panel) return;
    panel.scrollTop = 0;
  }, [mobilePanel, activeWeavePlan?.id, weaveError]);
  const weaveEntryDiscovery =
    exploreSnapshot.discoveries.find((discovery) =>
      Boolean(discovery.worldNodeId && discovery.attunementLevel),
    ) ??
    exploreSnapshot.discoveries.find((discovery) => discovery.state === 'eligible') ??
    exploreSnapshot.discoveries.find(
      (discovery) => discovery.state === 'claimed' && !discovery.attunementLevel,
    ) ??
    exploreSnapshot.discoveries.find((discovery) => discovery.state === 'nearby');
  const featuredRoute =
    exploreSnapshot.routeContracts?.find((route) => route.state === 'active') ??
    exploreSnapshot.routeContracts?.find((route) => route.state === 'available') ??
    exploreSnapshot.routeContracts?.[0];
  const interactionRadius = Math.round(exploreSnapshot.interactionRadiusM);
  const guideActive = guidedLoopActive(location.search);

  const refreshLocalWorld = useCallback(
    async (position: JourneyMapPosition, options?: { force?: boolean; signal?: AbortSignal }) => {
      const refreshKey = localExploreRefreshKey(position);
      if (!options?.force && nearbyRefreshKey.current === refreshKey) return undefined;
      nearbyRefreshKey.current = refreshKey;
      const refreshSequence = nearbyRefreshSequence.current + 1;
      nearbyRefreshSequence.current = refreshSequence;
      try {
        const localWorld = await loadNearbyExplore(position, { signal: options?.signal });
        if (
          options?.signal?.aborted ||
          nearbyRefreshKey.current !== refreshKey ||
          nearbyRefreshSequence.current !== refreshSequence
        )
          return undefined;
        setLocalWorldCenter(position.coordinates);
        setNearbyDiscoveries(localWorld.discoveries);
        setNearbyEnergyLinks(localWorld.energyLinks);
        setNearbyWeaves(localWorld.weaves);
        setNearbyRoutes(localWorld.routeContracts);
        setNearbyError(undefined);
        return localWorld;
      } catch {
        if (
          options?.signal?.aborted ||
          nearbyRefreshKey.current !== refreshKey ||
          nearbyRefreshSequence.current !== refreshSequence
        )
          return undefined;
        setNearbyError('Nearby stops could not load. Try again when connected.');
        return undefined;
      }
    },
    [],
  );

  const requestBrowserLocation = useCallback(async () => {
    if (
      journey.active ||
      journey.safety.mode === 'journey-passive' ||
      browserLocation.phase === 'locating'
    )
      return;
    if (!navigator.geolocation) {
      setBrowserLocation({
        error:
          'This browser cannot provide a location. The street map and Daily Codex remain available.',
        phase: 'error',
      });
      return;
    }
    setBrowserLocation((current) => ({ ...current, error: undefined, phase: 'locating' }));
    setNearbyError(undefined);
    try {
      const position = await requestInitialForegroundPosition(navigator.geolocation);
      const mappedPosition = mapPositionFromGeolocation(position);
      setBrowserLocation({ phase: 'located', position: mappedPosition });
      void refreshLocalWorld(mappedPosition);
    } catch (caught) {
      const message =
        caught instanceof Error ? caught.message : 'Your browser location is unavailable.';
      setBrowserLocation((current) =>
        current.position ? { ...current, phase: 'located' } : { error: message, phase: 'error' },
      );
      setNearbyError(message);
    }
  }, [browserLocation.phase, journey.active, journey.safety.mode, refreshLocalWorld]);

  const automaticLocationRequested = useRef(false);
  useEffect(() => {
    if (
      !requiresCurrentLocation ||
      !journey.error ||
      journey.active ||
      displayedPosition ||
      automaticLocationRequested.current
    )
      return;
    automaticLocationRequested.current = true;
    void requestBrowserLocation();
  }, [
    displayedPosition,
    journey.active,
    journey.error,
    requestBrowserLocation,
    requiresCurrentLocation,
  ]);

  useEffect(() => {
    presentationTrackerRef.current?.stop();
    presentationTrackerRef.current = null;
    if (
      fixture ||
      journey.active ||
      !requiresCurrentLocation ||
      browserLocation.phase !== 'located' ||
      !navigator.geolocation
    ) {
      return;
    }
    const tracker = createForegroundPositionTracker({
      geolocation: navigator.geolocation,
      onPosition: (position) => {
        setBrowserLocation({ phase: 'located', position });
        void refreshLocalWorld(position);
      },
      onError: (message) =>
        setBrowserLocation((current) =>
          current.position
            ? { ...current, error: message, phase: 'located' }
            : { error: message, phase: 'error' },
        ),
      document,
    });
    presentationTrackerRef.current = tracker;
    tracker.start();
    return () => {
      tracker.stop();
      if (presentationTrackerRef.current === tracker) presentationTrackerRef.current = null;
    };
  }, [browserLocation.phase, fixture, journey.active, refreshLocalWorld, requiresCurrentLocation]);

  useEffect(() => {
    const position = journey.currentPosition;
    if (fixture || !journey.active || !position) return;
    let cancelled = false;
    queueMicrotask(() => {
      if (!cancelled) void refreshLocalWorld(position);
    });
    return () => {
      cancelled = true;
    };
  }, [fixture, journey.active, journey.currentPosition, refreshLocalWorld]);

  useEffect(() => {
    if (fixture || !journey.active || !journey.stationaryConfirmed) {
      stationaryNearbyRefreshCompleted.current = false;
      return;
    }
    if (
      stationaryNearbyRefreshCompleted.current ||
      !selectedDetailsOpen ||
      !journey.currentPosition ||
      (selected?.state !== 'nearby' && selected?.state !== 'queued')
    )
      return;
    stationaryNearbyRefreshCompleted.current = true;
    const position = journey.currentPosition;
    let cancelled = false;
    queueMicrotask(() => {
      if (!cancelled) void refreshLocalWorld(position, { force: true });
    });
    return () => {
      cancelled = true;
    };
  }, [
    fixture,
    journey.active,
    journey.currentPosition,
    journey.stationaryConfirmed,
    refreshLocalWorld,
    selected?.state,
    selectedDetailsOpen,
  ]);

  function claimSelected() {
    if (!selected) return;
    command.mutate(
      { type: 'explore.claim', commandId: commandId('explore.claim'), discoveryId: selected.id },
      { onSuccess: () => navigate(guidedPath('/decode', guideActive)) },
    );
  }

  const verifySelectedStop = useCallback(
    async (signal: AbortSignal) => {
      if (!selected || !displayedPosition) return 'interrupted' as const;
      const verifiedPosition = await verifyCurrentPosition(selected.id, signal);
      if (!verifiedPosition || signal.aborted) return 'interrupted' as const;
      const localWorld = await refreshLocalWorld(verifiedPosition, { force: true, signal });
      if (!localWorld) return 'interrupted' as const;
      const refreshed = localWorld.discoveries.find((discovery) => discovery.id === selected.id);
      if (refreshed?.state === 'eligible') return 'ready' as const;
      const distance = discoveryDistanceM(selected, verifiedPosition);
      return distance !== undefined && distance > interactionRadius
        ? ('closer' as const)
        : ('waiting' as const);
    },
    [selected, displayedPosition, verifyCurrentPosition, refreshLocalWorld, interactionRadius],
  );
  const stopCheck = useLocationCheck({
    key: selected?.id,
    enabled: Boolean(
      !fixture &&
      selectedDetailsOpen &&
      online &&
      journey.foregroundVisible &&
      journey.active &&
      journey.phase === 'recording' &&
      journey.safety.mode !== 'journey-passive' &&
      selected &&
      (selected.state === 'nearby' || selected.state === 'queued') &&
      selectedDistanceM !== undefined &&
      selectedDistanceM <= interactionRadius,
    ),
    verify: verifySelectedStop,
    budget: locationCheckBudget,
  });

  function beginRemoteExpedition() {
    command.mutate(
      { type: 'explore.remote', commandId: commandId('explore.remote') },
      { onSuccess: () => navigate(guidedPath('/decode', guideActive)) },
    );
  }

  async function beginRoute(route: WorldRouteContractView) {
    if (!journey.active || !journey.journeyId || !displayedPosition) {
      setMobilePanel('journey');
      if (!journey.active) await journey.beginJourney();
      return;
    }
    setRoutePendingId(route.id);
    setRouteError(undefined);
    try {
      await startWorldRoute(journey.journeyId, route.id);
      nearbyRefreshKey.current = undefined;
      void refreshLocalWorld(displayedPosition);
    } catch (caught) {
      setRouteError(caught instanceof Error ? caught.message : 'This route could not be started.');
    } finally {
      setRoutePendingId(undefined);
    }
  }

  async function chooseWeaveOrigin(discovery: DiscoveryMarkerView) {
    if (!discovery.worldNodeId || !discovery.attunementLevel || weavePending) return;
    const generation = ++weaveTargetsGeneration.current;
    const isCurrent = () =>
      generation === weaveTargetsGeneration.current && document.visibilityState !== 'hidden';
    setWeavePending(true);
    setWeaveError(undefined);
    try {
      const response = await loadWeaveTargets(discovery.worldNodeId);
      if (!isCurrent()) return;
      setWeaveOriginId(discovery.worldNodeId);
      setWeaveTargetId(undefined);
      setWeaveTargets(response.targets);
      setSelectedDetailsOpen(false);
      setMobilePanel('weaves');
    } catch {
      if (isCurrent()) setWeaveError('Your connections could not load. Try again when connected.');
    } finally {
      if (isCurrent()) setWeavePending(false);
    }
  }

  async function planWeaveTarget(destinationNodeId: string) {
    if (
      !weaveOriginId ||
      !displayedPosition ||
      !online ||
      weavePlanInFlight.current ||
      !journey.foregroundVisible ||
      journey.safety.mode === 'journey-passive' ||
      journey.phase === 'recovery'
    )
      return;
    weavePlanInFlight.current = true;
    setWeavePending(true);
    setWeaveError(undefined);
    try {
      const plannedWeave = await planKnowledgeWeave(weaveOriginId, destinationNodeId);
      // The durable plan remains saved if the player leaves Explore while the
      // response is pending. Never start location tracking on another surface.
      if (!exploreMounted.current) return;
      setNearbyWeaves((current) => [
        ...current.filter((item) => item.id !== plannedWeave.id),
        plannedWeave,
      ]);
      setWeaveOriginId(undefined);
      setWeaveTargetId(undefined);
      setWeaveTargets([]);
      setSelectedDetailsOpen(false);
      setMobilePanel(undefined);
      nearbyRefreshKey.current = undefined;
      void refreshLocalWorld(displayedPosition);
      // The explicit route confirmation also starts its fresh proof-bound walk.
      // Existing Journey final samples/rewards must finish successfully first.
      const currentJourney = latestJourney.current;
      if (
        document.visibilityState !== 'hidden' &&
        currentJourney.foregroundVisible &&
        currentJourney.safety.mode !== 'journey-passive' &&
        currentJourney.phase !== 'recovery'
      ) {
        await currentJourney.beginWeaveWalk();
      }
    } catch {
      setWeaveError('This connection could not start. Try another destination or reconnect.');
    } finally {
      weavePlanInFlight.current = false;
      setWeavePending(false);
    }
  }

  async function planSelectedWeave() {
    if (!selectedWeaveTarget || selectedWeaveTarget.state === 'woven') return;
    await planWeaveTarget(selectedWeaveTarget.nodeId);
  }

  async function cancelActiveWeave() {
    if (!activeWeavePlan || !displayedPosition) return;
    setWeavePending(true);
    setWeaveError(undefined);
    try {
      await cancelKnowledgeWeave(activeWeavePlan.id);
      nearbyRefreshKey.current = undefined;
      void refreshLocalWorld(displayedPosition);
    } catch {
      setWeaveError('This connection could not be cancelled. Try again when connected.');
    } finally {
      setWeavePending(false);
    }
  }

  async function beginWeaveChallenge() {
    if (!activeWeavePlan || !online || journey.safety.mode === 'journey-passive') return;
    setWeavePending(true);
    setWeaveError(undefined);
    try {
      await command.mutateAsync({
        type: 'decode.weave.start',
        commandId: commandId('decode.weave.start'),
        weaveId: activeWeavePlan.id,
      });
      if (exploreMounted.current) navigate('/decode');
    } catch {
      setWeaveError('Your connection puzzle could not open. Reconnect and try again.');
    } finally {
      setWeavePending(false);
    }
  }

  const weaveDestination = exploreSnapshot.discoveries.find(
    (discovery) => discovery.worldNodeId === activeWeavePlan?.destinationNodeId,
  );
  const weaveDestinationDistance = weaveDestination
    ? discoveryDistanceM(weaveDestination, displayedPosition)
    : undefined;
  const verifyWeaveArrival = useCallback(
    async (signal: AbortSignal) => {
      if (!weaveDestination || !activeWeavePlan) return 'interrupted' as const;
      const position = await verifyCurrentPosition(weaveDestination.id, signal);
      if (!position || signal.aborted) return 'interrupted' as const;
      const refreshed = await refreshLocalWorld(position, { force: true, signal });
      if (!refreshed) return 'interrupted' as const;
      const weave = refreshed.weaves?.find((item) => item.id === activeWeavePlan.id);
      return weave?.challengeState === 'ready' || weave?.challengeState === 'active'
        ? ('ready' as const)
        : ('waiting' as const);
    },
    [activeWeavePlan, weaveDestination, verifyCurrentPosition, refreshLocalWorld],
  );
  const arrivalCheck = useLocationCheck({
    key: activeWeavePlan?.id,
    enabled: Boolean(
      activeWeavePlan?.challengeState === 'journey_required' &&
      weaveWalkStarted &&
      !selectedDetailsOpen &&
      online &&
      journey.foregroundVisible &&
      journey.phase === 'recording' &&
      journey.safety.mode !== 'journey-passive' &&
      weaveDestinationDistance !== undefined &&
      weaveDestinationDistance <= interactionRadius,
    ),
    verify: verifyWeaveArrival,
    budget: locationCheckBudget,
  });

  const weaveVisitsConfirmed =
    activeWeavePlan?.challengeState === 'ready' || activeWeavePlan?.challengeState === 'active';
  const weaveNextStep = activeWeavePlan
    ? activeWeavePlan.challengeState === 'unavailable'
      ? 'Route unavailable · choose another connection'
      : weaveVisitsConfirmed
        ? activeWeavePlan.challengeState === 'active'
          ? '3 · Continue your saved connection puzzle'
          : '3 · Both visits confirmed · solve the connection'
        : !weaveWalkStarted
          ? 'Start a fresh walk to confirm your visits'
          : activeWeavePlan.visitStage === 'destination_required'
            ? `2 · Reach ${activeWeavePlan.destinationName} and pause safely`
            : activeWeavePlan.visitStage === 'arrived'
              ? 'Both visits recorded · waiting for arrival confirmation'
              : activeWeavePlan.visitStage === 'origin_required'
                ? `1 · Reach ${activeWeavePlan.sourceName} and pause safely`
                : 'Visit the origin, then the destination · pause safely at each'
    : undefined;

  async function startWeaveWalk() {
    if (weavePending || !online || journey.safety.mode === 'journey-passive') return;
    setWeavePending(true);
    setWeaveError(undefined);
    setSelectedDetailsOpen(false);
    setMobilePanel(undefined);
    try {
      await journey.beginWeaveWalk();
    } finally {
      setWeavePending(false);
    }
  }

  // Readiness follows accepted foreground GPS, at most once per 15 seconds.
  // This reads server proof; proximity alone never marks a visit or grants rewards.
  const weaveRefreshAt = useRef(0);
  const weaveRefreshInFlight = useRef(false);
  useEffect(() => {
    if (
      !activeWeavePlan ||
      activeWeavePlan.challengeState !== 'journey_required' ||
      !weaveWalkStarted ||
      !online ||
      !journey.foregroundVisible ||
      journey.safety.mode === 'journey-passive' ||
      !displayedPosition ||
      !journey.verificationRevision ||
      weaveRefreshInFlight.current
    )
      return;
    const delay = Math.max(0, 15_000 - (Date.now() - weaveRefreshAt.current));
    const timer = window.setTimeout(() => {
      weaveRefreshAt.current = Date.now();
      weaveRefreshInFlight.current = true;
      void refreshLocalWorld(displayedPosition, { force: true }).finally(() => {
        weaveRefreshInFlight.current = false;
      });
    }, delay);
    return () => window.clearTimeout(timer);
  }, [
    activeWeavePlan,
    displayedPosition,
    journey.foregroundVisible,
    journey.safety.mode,
    journey.verificationRevision,
    online,
    refreshLocalWorld,
    weaveWalkStarted,
  ]);

  return (
    <section
      className="player-screen explore-screen"
      ref={exploreLayoutRef}
      data-discovery-reveal={revealDiscovery ? 'active' : undefined}
      data-guided={guideActive || undefined}
      data-journey-state={journey.safety.mode}
      data-location-phase={browserLocation.phase}
      data-weave-active={Boolean(activeWeavePlan) || undefined}
      data-visual-ready={visualState && mapReady ? 'true' : undefined}
      data-visual-state={visualState}
    >
      {guideActive && (
        <GuideCoachmark
          serverCompleted={snapshot.profile.onboardingCompleted}
          serverStep={snapshot.profile.onboardingStep}
          step="1 of 4 · Explore"
          title={journey.active ? 'Let the journey record safely' : 'Reveal one nearby fragment'}
        >
          <p>
            {journey.safety.mode === 'journey-passive'
              ? 'No interaction is available while movement is above the safety threshold. Stop only when safe.'
              : journey.active
                ? 'End the Journey while safely stationary, select an eligible fragment, then secure it for Decode.'
                : 'Start Journey for the movement path, or use the no-location Daily Codex option.'}
          </p>
        </GuideCoachmark>
      )}
      <header className="scene-title scene-title--explore">
        <span className="realm-screen__eyebrow">
          Expedition Codex · {exploreSnapshot.regionName}
        </span>
        <h1>Reveal {exploreSnapshot.regionName}</h1>
        <p>
          {journey.active
            ? displayedPosition
              ? `Journey following your browser location · ±${Math.round(displayedPosition.accuracyM)} m.`
              : 'Journey preparing your foreground location.'
            : browserLocation.phase === 'locating'
              ? 'Finding this browser on the street map…'
              : browserLocation.phase === 'located' && displayedPosition
                ? `${nearbyDiscoveries.length || 'Local'} encounters around your streets · ±${Math.round(displayedPosition.accuracyM)} m.`
                : browserLocation.phase === 'error'
                  ? 'Location unavailable · street review and Daily Codex still work.'
                  : `${snapshot.explore.uniqueCells} knowledge cell revealed · use the target control for your streets.`}
        </p>
        <div className="scene-title--explore__actions">
          {!journey.active && (
            <>
              <PrimaryActionButton
                disabled={!online || journey.phase === 'starting' || command.isPending}
                loading={journey.phase === 'starting'}
                onClick={() => void journey.beginJourney()}
              >
                Start Journey
              </PrimaryActionButton>
              <span className="remote-expedition-action">
                <small>No location required</small>
                <SecondaryActionButton
                  aria-label="Open or resume daily Codex expedition without location access"
                  disabled={!online || journey.phase === 'starting' || command.isPending}
                  onClick={beginRemoteExpedition}
                >
                  Open Daily Codex
                </SecondaryActionButton>
              </span>
            </>
          )}
          {journey.active && journey.safety.mode !== 'journey-passive' && (
            <span className="journey-stop-actions">
              <SecondaryActionButton
                disabled={journey.phase === 'stopping'}
                onClick={() => void journey.stopJourney()}
              >
                End Journey
              </SecondaryActionButton>
              <button
                className="journey-cancel-action"
                disabled={journey.phase === 'stopping'}
                onClick={() => void journey.cancelJourney()}
                type="button"
              >
                Cancel · no reward
              </button>
            </span>
          )}
        </div>
      </header>
      {waitingForCurrentLocation ? (
        <div
          aria-live={browserLocation.phase === 'error' ? undefined : 'polite'}
          className="explore-location-gate"
          role={browserLocation.phase === 'error' ? 'alert' : 'status'}
        >
          <span aria-hidden="true" className="explore-location-gate__beacon" />
          <span className="realm-screen__eyebrow">Your streets first</span>
          <h2>
            {browserLocation.phase === 'error'
              ? 'Current location is unavailable'
              : 'Finding your current location'}
          </h2>
          <p>{browserLocation.error ?? 'Allow location to discover the places around you.'}</p>
          {browserLocation.phase === 'error' && (
            <div className="explore-location-gate__actions">
              <PrimaryActionButton
                disabled={!online}
                onClick={() => {
                  automaticLocationRequested.current = false;
                  void requestBrowserLocation();
                }}
              >
                Try current location again
              </PrimaryActionButton>
              <SecondaryActionButton
                disabled={!online || command.isPending}
                onClick={beginRemoteExpedition}
              >
                Open Daily Codex instead
              </SecondaryActionButton>
            </div>
          )}
          <small>Location is used only while Explore is open.</small>
        </div>
      ) : !initialWorldReady ? (
        <div className="explore-location-gate" role="status">
          Preparing nearby places…
        </div>
      ) : (
        <ExploreMap
          activeRoute={journey.activeRoute}
          currentPosition={displayedPosition}
          journeyActive={journey.active}
          locationPhase={displayedPosition ? 'located' : browserLocation.phase}
          onDiscoverySelected={onDiscoverySelected}
          onRequestLocation={() => void requestBrowserLocation()}
          onReady={onMapReady}
          onUnavailable={onMapUnavailable}
          onViewport={viewportWorld.onViewport}
          passive={journey.safety.mode === 'journey-passive'}
          playerAvatarLabel={snapshot.profile.avatarLabel}
          playerAvatarUrl={snapshot.profile.avatarAssetUrl}
          qualityTier={settings.qualityTier}
          revealDiscoveryId={revealDiscovery?.id}
          selectedDiscoveryId={selectedId}
          snapshot={exploreSnapshot}
        />
      )}
      <nav aria-label="Explore tools" className="explore-mobile-hud">
        <button
          aria-expanded={mobilePanel === 'journey'}
          aria-label={journey.active ? 'Open active Journey controls' : 'Start Journey'}
          data-active={journey.active || undefined}
          disabled={!online || journey.phase === 'starting' || journey.phase === 'stopping'}
          onClick={() => {
            if (journey.active) {
              setMobilePanel((current) => (current === 'journey' ? undefined : 'journey'));
              return;
            }
            setMobilePanel('journey');
            void journey.beginJourney();
          }}
          type="button"
        >
          <WalkingIcon />
          <span>{journey.active ? 'Journey' : 'Go'}</span>
        </button>
        <button
          aria-expanded={mobilePanel === 'weaves'}
          aria-label="Open player-selected Knowledge Weaves"
          data-active={Boolean(activeWeavePlan) || undefined}
          onClick={() => {
            setSelectedDetailsOpen(false);
            setMobilePanel((current) => (current === 'weaves' ? undefined : 'weaves'));
          }}
          type="button"
        >
          <RouteChainIcon />
          <span>Weave</span>
        </button>
        <button
          aria-expanded={mobilePanel === 'nearby'}
          aria-label={`Open ${nearestListDiscoveries.length} nearby encounters, nearest first`}
          onClick={() => setMobilePanel((current) => (current === 'nearby' ? undefined : 'nearby'))}
          type="button"
        >
          <NearbyIcon />
          <span>Nearby</span>
        </button>
        <span
          aria-label={
            displayedPosition
              ? `${liveGpsActive ? 'Live GPS' : 'Location'} accuracy ${Math.round(displayedPosition.accuracyM)} metres`
              : fixture
                ? 'Demo world in Amsterdam; not your location'
                : 'Location not centered'
          }
          className="explore-mobile-hud__location"
          data-live={liveGpsActive || undefined}
        >
          <i aria-hidden="true" />
          {displayedPosition
            ? `±${Math.round(displayedPosition.accuracyM)}m`
            : fixture
              ? 'Demo · Amsterdam'
              : 'Locate'}
        </span>
      </nav>
      {mobilePanel === 'journey' && journey.safety.mode !== 'journey-passive' && (
        <aside
          aria-labelledby="explore-journey-panel-title"
          className="explore-mobile-panel"
          data-mobile-panel="journey"
        >
          <header>
            <span>
              <small>Safe exploration</small>
              <h2 id="explore-journey-panel-title">
                {journey.active ? 'Journey in progress' : 'Choose how to explore'}
              </h2>
            </span>
            <button
              aria-label="Close Journey options"
              className="explore-mobile-panel__close"
              onClick={() => {
                invalidateWeaveTargets();
                setMobilePanel(undefined);
              }}
              type="button"
            >
              ×
            </button>
          </header>
          <p>
            {journey.error
              ? 'Your walk was interrupted. Reconnect and try again.'
              : (journey.capabilityNotice ??
                (journey.active
                  ? 'Your position is live. Reach a stop safely to play.'
                  : 'Explore your streets with Journey, or discover a glyph in the Daily Codex.'))}
          </p>
          <div className="explore-mobile-panel__actions">
            {!journey.active && (
              <>
                <PrimaryActionButton
                  aria-label="Try Journey again"
                  disabled={!online || journey.phase === 'starting' || command.isPending}
                  loading={journey.phase === 'starting'}
                  onClick={() => void journey.beginJourney()}
                >
                  Start Journey
                </PrimaryActionButton>
                <SecondaryActionButton
                  disabled={!online || journey.phase === 'starting' || command.isPending}
                  onClick={beginRemoteExpedition}
                >
                  <span className="explore-daily-label">
                    <CodexExpeditionIcon />
                    Daily Codex
                  </span>
                </SecondaryActionButton>
              </>
            )}
            {journey.active && (
              <>
                <PrimaryActionButton
                  disabled={journey.phase === 'stopping'}
                  loading={journey.phase === 'stopping'}
                  onClick={() => void journey.stopJourney()}
                >
                  End Journey
                </PrimaryActionButton>
                <button
                  className="journey-cancel-action"
                  disabled={journey.phase === 'stopping'}
                  onClick={() => void journey.cancelJourney()}
                  type="button"
                >
                  Cancel · no reward
                </button>
              </>
            )}
            {Boolean(exploreSnapshot.routeContracts?.length) && (
              <SecondaryActionButton onClick={() => setMobilePanel('routes')}>
                Browse World routes
              </SecondaryActionButton>
            )}
          </div>
        </aside>
      )}
      {mobilePanel === 'nearby' && journey.safety.mode !== 'journey-passive' && (
        <aside
          aria-labelledby="explore-nearby-panel-title"
          className="explore-mobile-panel"
          data-mobile-panel="nearby"
        >
          <header>
            <span>
              <small>Your next discovery</small>
              <h2 id="explore-nearby-panel-title">Nearby encounters</h2>
            </span>
            <button
              aria-label="Close nearby encounters"
              className="explore-mobile-panel__close"
              onClick={() => setMobilePanel(undefined)}
              type="button"
            >
              ×
            </button>
          </header>
          <ul className="explore-mobile-nearby">
            {nearestListDiscoveries.map((discovery) => (
              <li key={discovery.id}>
                <button
                  aria-label={`${discovery.title}. ${
                    discoveryDistanceM(discovery, displayedPosition) === undefined
                      ? ''
                      : `${discoveryDistanceM(discovery, displayedPosition)} metres away. `
                  }${discoveryStatus(discovery, interactionRadius)}`}
                  data-distance-m={discoveryDistanceM(discovery, displayedPosition)}
                  data-mobile-discovery-id={discovery.id}
                  data-state={discovery.state}
                  onClick={() => onDiscoverySelected(discovery.id)}
                  type="button"
                >
                  <WorldNodeArt
                    kind={discovery.nodeKind}
                    state={discovery.state}
                    decorative
                    className="explore-nearby-art"
                  />
                  <span>
                    <strong>{discovery.title}</strong>
                    {discovery.nodeKind && (
                      <span className="explore-nearby-class">
                        {worldNodeLabel(discovery.nodeKind)}
                      </span>
                    )}
                    <small>
                      {discoveryDistanceM(discovery, displayedPosition) === undefined
                        ? ''
                        : `${discoveryDistanceM(discovery, displayedPosition)} m · `}
                      {discoveryStatus(discovery, interactionRadius)}
                    </small>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {nearestListDiscoveries.length === 0 && (
            <div className="explore-empty-state">
              <WorldNodeArt
                kind="verdant_waypoint"
                decorative
                className="explore-empty-state__art"
              />
              <strong>No stops nearby yet</strong>
              <p>
                {nearbyError
                  ? 'The nearby places could not load. Try your location again.'
                  : 'Explore another area, or discover a glyph in the Daily Codex.'}
              </p>
              <SecondaryActionButton
                disabled={!online || command.isPending}
                onClick={beginRemoteExpedition}
              >
                Open Daily Codex
              </SecondaryActionButton>
              <SecondaryActionButton
                disabled={!online}
                onClick={() => void requestBrowserLocation()}
              >
                Refresh my location
              </SecondaryActionButton>
            </div>
          )}
          {Boolean(exploreSnapshot.energyLinks?.length) && (
            <p>
              {exploreSnapshot.energyLinks?.filter((link) => link.state === 'awakened').length ?? 0}{' '}
              of {exploreSnapshot.energyLinks?.length ?? 0} nearby Knowledge Currents awakened. Walk
              between linked nodes and restore both glyphs to carry the energy through.
            </p>
          )}
        </aside>
      )}
      {activeWeavePlan &&
        !mobilePanel &&
        !selectedDetailsOpen &&
        journey.safety.mode !== 'journey-passive' && (
          <aside className="explore-weave-hud" aria-label="Active Weave">
            <div className="explore-weave-hud__route">
              <RouteChainIcon />
              <strong>
                {activeWeavePlan.sourceName} → {activeWeavePlan.destinationName}
              </strong>
              <button
                type="button"
                aria-label="Show Weave details"
                onClick={() => setMobilePanel('weaves')}
              >
                Details
              </button>
            </div>
            <p className="explore-weave-hud__step" role="status">
              {weaveNextStep}
            </p>
            {arrivalCheck.pending && <small role="status">Checking arrival…</small>}
            <div className="explore-weave-hud__actions">
              {activeWeavePlan.challengeState === 'ready' ||
              activeWeavePlan.challengeState === 'active' ? (
                <PrimaryActionButton
                  disabled={weavePending || command.isPending || !online}
                  loading={weavePending}
                  onClick={() => void beginWeaveChallenge()}
                >
                  {activeWeavePlan.challengeState === 'active'
                    ? 'Continue puzzle'
                    : 'Solve connection'}
                </PrimaryActionButton>
              ) : activeWeavePlan.challengeState === 'unavailable' ? (
                <SecondaryActionButton onClick={() => setMobilePanel('weaves')}>
                  Change connection
                </SecondaryActionButton>
              ) : !weaveWalkStarted ? (
                <PrimaryActionButton
                  disabled={
                    weavePending ||
                    !online ||
                    journey.phase === 'starting' ||
                    journey.phase === 'stopping'
                  }
                  loading={weavePending}
                  onClick={() => void startWeaveWalk()}
                >
                  Start walk
                </PrimaryActionButton>
              ) : arrivalCheck.outcome === 'interrupted' || arrivalCheck.delayed ? (
                <SecondaryActionButton
                  disabled={!arrivalCheck.canRetry}
                  loading={arrivalCheck.pending}
                  onClick={() => void arrivalCheck.check()}
                >
                  Try location again
                </SecondaryActionButton>
              ) : null}
            </div>
            {(weaveError || journey.error) && (
              <small role="alert">
                {weaveError || 'Your walk was interrupted. Reconnect to continue.'}
              </small>
            )}
          </aside>
        )}
      {mobilePanel === 'weaves' && journey.safety.mode !== 'journey-passive' && (
        <aside
          aria-labelledby="explore-weave-panel-title"
          className="explore-mobile-panel"
          data-mobile-panel="weaves"
          data-active-plan={Boolean(activeWeavePlan) || undefined}
          data-weave-stage={
            activeWeavePlan
              ? 'planned'
              : weaveOrigin
                ? weaveTargets.length
                  ? 'destination'
                  : 'empty'
                : 'intro'
          }
          ref={weavePanelRef}
        >
          <header>
            <span>
              <small>
                {activeWeavePlan
                  ? 'Your walking connection'
                  : weaveOrigin && weaveTargets.length
                    ? 'Choose a destination'
                    : 'Your walking connections'}
              </small>
              <h2 id="explore-weave-panel-title">
                {activeWeavePlan
                  ? 'Weave progress'
                  : weaveOrigin && !weaveTargets.length
                    ? 'No destinations available'
                    : 'Connect two places'}
              </h2>
            </span>
            <button
              aria-label="Close Knowledge Weave"
              className="explore-mobile-panel__close"
              onClick={() => {
                invalidateWeaveTargets();
                setMobilePanel(undefined);
              }}
              type="button"
            >
              ×
            </button>
          </header>
          {activeWeavePlan ? (
            <>
              <div className="knowledge-weave-actions">
                {activeWeavePlan.challengeState === 'ready' ||
                activeWeavePlan.challengeState === 'active' ? (
                  <PrimaryActionButton
                    disabled={weavePending || command.isPending || !online}
                    loading={weavePending}
                    onClick={() => void beginWeaveChallenge()}
                  >
                    {activeWeavePlan.challengeState === 'active'
                      ? 'Continue puzzle'
                      : 'Solve connection'}
                  </PrimaryActionButton>
                ) : !weaveWalkStarted && activeWeavePlan.challengeState !== 'unavailable' ? (
                  <PrimaryActionButton
                    disabled={weavePending || !online || !displayedPosition}
                    loading={weavePending}
                    onClick={() => void startWeaveWalk()}
                  >
                    Start walk
                  </PrimaryActionButton>
                ) : null}
                <SecondaryActionButton onClick={() => setMobilePanel(undefined)}>
                  Show map
                </SecondaryActionButton>
              </div>
              <div
                className="knowledge-weave-body"
                tabIndex={0}
                aria-label="Weave route and instructions"
              >
                {!online && (
                  <p role="status">Reconnect to continue this Weave. Your plan is saved.</p>
                )}
                {online && !displayedPosition && (
                  <p role="status">Allow location in Journey options to check this route.</p>
                )}
                <div className="knowledge-weave-summary" data-status={activeWeavePlan.status}>
                  <span aria-hidden="true">◇━━◇</span>
                  <strong>
                    {activeWeavePlan.sourceName} → {activeWeavePlan.destinationName}
                  </strong>
                  <small>{activeWeavePlan.distanceM} m · your chosen connection</small>
                </div>
                <p className="knowledge-weave-next-step" role="status">
                  {weaveNextStep}
                </p>
                <WeaveProgress weave={activeWeavePlan} />
                <WeaveRouteNotice
                  kind={activeWeavePlan.routeKind}
                  attribution={activeWeavePlan.routeAttribution}
                  datasetRelease={activeWeavePlan.routeDatasetRelease}
                />
                <details className="knowledge-weave-instructions">
                  <summary>How connections work</summary>
                  <p>
                    Walk to the first stop, then the second. Keep the game open and pause safely at
                    each place. Your puzzle appears here when both visits are confirmed.
                  </p>
                  <p>Solve it to light your connection. Any other unfinished puzzle stays saved.</p>
                </details>
                <SecondaryActionButton
                  disabled={weavePending || !online || !displayedPosition}
                  onClick={() => void cancelActiveWeave()}
                >
                  Cancel this plan
                </SecondaryActionButton>
                <a className="explore-constellations-link" href="/codex#constellations">
                  Your Codex constellations <span aria-hidden="true">→</span>
                </a>
              </div>
            </>
          ) : weaveOrigin ? (
            <>
              <div className="knowledge-weave-summary" data-status="origin">
                <span aria-hidden="true">◇···</span>
                <strong>{weaveOrigin.title}</strong>
                <small>
                  {weaveTargets.length} {weaveTargets.length === 1 ? 'place' : 'places'} you can
                  connect
                </small>
              </div>
              {weaveTargets.length > 0 ? (
                <ul aria-label="Linkable Wayfinder Imprints" className="knowledge-weave-targets">
                  {weaveTargets.map((target) => (
                    <li key={target.nodeId}>
                      <button
                        disabled={weavePending || !online || !displayedPosition}
                        aria-pressed={target.nodeId === requestedWeaveTargetId}
                        onClick={() => {
                          setWeaveTargetId(target.nodeId);
                          setWeaveError(undefined);
                          const targetDiscovery = exploreSnapshot.discoveries.find(
                            (item) => item.worldNodeId === target.nodeId,
                          );
                          if (targetDiscovery) setSelectedId(targetDiscovery.id);
                        }}
                        type="button"
                      >
                        <WorldNodeArt
                          kind={target.nodeKind}
                          decorative
                          className="explore-nearby-art"
                        />
                        <span>
                          <strong>{target.name}</strong>
                          <small>
                            {target.distanceM} m from origin · {worldNodeLabel(target.nodeKind)}
                          </small>
                          <WeaveRouteNotice kind={target.routeKind} />
                        </span>
                        <b>Choose</b>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="explore-empty-state">
                  <strong>No available connection from this origin</strong>
                  <p>
                    Explore other stops. A previously visited place also needs a walking route and
                    available connection capacity.
                  </p>
                  <PrimaryActionButton
                    onClick={() => {
                      setWeaveOriginId(undefined);
                      setWeaveTargetId(undefined);
                      setWeaveTargets([]);
                      setWeaveError(undefined);
                      setMobilePanel('nearby');
                    }}
                  >
                    Find another stop
                  </PrimaryActionButton>
                  <SecondaryActionButton
                    disabled={!online || weavePending}
                    onClick={() => void chooseWeaveOrigin(weaveOrigin)}
                  >
                    Refresh destinations
                  </SecondaryActionButton>
                  <details className="knowledge-weave-instructions">
                    <summary>Why a stop may be missing</summary>
                    <p>
                      You need to have visited the place. It must also have an available walking
                      route and room for another connection.
                    </p>
                  </details>
                </div>
              )}
              <div className="knowledge-weave-target-actions">
                {selectedWeaveTarget && selectedWeaveTarget.state !== 'woven' && (
                  <PrimaryActionButton
                    disabled={
                      weavePending || !online || !displayedPosition || journey.phase === 'recovery'
                    }
                    loading={weavePending}
                    onClick={() => void planSelectedWeave()}
                  >
                    Walk this route
                  </PrimaryActionButton>
                )}
                <SecondaryActionButton
                  onClick={() => {
                    setWeaveOriginId(undefined);
                    setWeaveTargetId(undefined);
                    setWeaveTargets([]);
                    setWeaveError(undefined);
                    setMobilePanel(undefined);
                  }}
                >
                  Cancel target choice
                </SecondaryActionButton>
              </div>
            </>
          ) : (
            <div className="knowledge-weave-onboarding">
              <div className="explore-weave-art" aria-hidden="true">
                <WorldNodeArt kind="glyph_archive" decorative />
                <span>✦</span>
                <WorldNodeArt kind="water_gate" decorative />
              </div>
              <p>Turn two places you have discovered into a glowing connection of your own.</p>
              <ol>
                <li>
                  <i aria-hidden="true">1</i>
                  Visit two stops.
                </li>
                <li>
                  <i aria-hidden="true">2</i>
                  Solve one stop and choose where to connect it.
                </li>
                <li>
                  <i aria-hidden="true">3</i>
                  Walk between them, then solve the connection puzzle.
                </li>
              </ol>
              {weaveEntryDiscovery && (
                <PrimaryActionButton onClick={() => onDiscoverySelected(weaveEntryDiscovery.id)}>
                  {weaveEntryDiscovery.attunementLevel
                    ? 'Choose a decoded origin'
                    : weaveEntryDiscovery.state === 'claimed'
                      ? 'Resume your Decode'
                      : 'Play a nearby stop first'}
                </PrimaryActionButton>
              )}
              {!weaveEntryDiscovery && (
                <div className="explore-empty-state">
                  <strong>Your first connection starts with a discovery</strong>
                  <p>Find a stop, visit safely, then restore its glyph.</p>
                  <PrimaryActionButton onClick={() => setMobilePanel('nearby')}>
                    Find nearby stops
                  </PrimaryActionButton>
                </div>
              )}
            </div>
          )}
          {!activeWeavePlan && (
            <a className="explore-constellations-link" href="/codex#constellations">
              Your Codex constellations <span aria-hidden="true">→</span>
            </a>
          )}
          {Boolean(exploreSnapshot.weaves?.some((weave) => weave.status === 'awakened')) && (
            <small>
              {exploreSnapshot.weaves?.filter((weave) => weave.status === 'awakened').length}{' '}
              currents awakened in this view.
            </small>
          )}
          {weaveError && (
            <p className="inline-error" role="alert">
              {weaveError}
            </p>
          )}
        </aside>
      )}
      {mobilePanel === 'routes' && journey.safety.mode !== 'journey-passive' && (
        <aside
          aria-labelledby="explore-routes-panel-title"
          className="explore-mobile-panel"
          data-mobile-panel="routes"
        >
          <header>
            <span>
              <small>Walk linked knowledge</small>
              <h2 id="explore-routes-panel-title">World routes</h2>
            </span>
            <button
              aria-label="Close World routes"
              className="explore-mobile-panel__close"
              onClick={() => setMobilePanel(undefined)}
              type="button"
            >
              ×
            </button>
          </header>
          <ul className="world-route-list">
            {exploreSnapshot.routeContracts?.map((route) => (
              <li data-state={route.state} key={route.id}>
                <span className="world-route-list__summary">
                  <small>
                    {route.familyCode} current ·{' '}
                    {Math.max(1, Math.round(route.distanceM / 100) / 10)} km
                  </small>
                  <strong>{route.title}</strong>
                  <span>
                    {route.currentStep} / {route.stops.length} stops · tier {route.requiredTier}
                  </span>
                </span>
                <ol aria-label={`${route.title} stops`}>
                  {route.stops.map((stop) => (
                    <li data-state={stop.state} key={stop.nodeId}>
                      <i aria-hidden="true">{stop.sequence}</i>
                      <span>
                        <strong>{stop.name}</strong>
                        <small>
                          {worldNodeLabel(stop.nodeKind)} · tier {stop.nodeTier}
                        </small>
                      </span>
                    </li>
                  ))}
                </ol>
                {route.state === 'available' && (
                  <PrimaryActionButton
                    disabled={!online || Boolean(routePendingId)}
                    loading={routePendingId === route.id}
                    onClick={() => void beginRoute(route)}
                  >
                    {journey.active ? 'Begin this route' : 'Start Journey for route'}
                  </PrimaryActionButton>
                )}
                {route.state === 'active' && <strong>Follow the highlighted stops in order</strong>}
                {route.state === 'completed' && <strong>Route restored</strong>}
              </li>
            ))}
          </ul>
          {routeError && (
            <p className="inline-error" role="alert">
              {routeError}
            </p>
          )}
          <small>
            Visit the highlighted places in order. Your progress is saved as you explore.
          </small>
        </aside>
      )}
      <div aria-label="Map knowledge legend" className="explore-legend">
        <span data-knowledge="discovered">
          <i aria-hidden="true" />
          Discovered
        </span>
        <span data-knowledge="seen">
          <i aria-hidden="true" />
          Seen
        </span>
        <span data-knowledge="unknown">
          <i aria-hidden="true" />
          Fogged
        </span>
      </div>
      {featuredRoute && journey.safety.mode !== 'journey-passive' && (
        <aside className="explore-route-card">
          <span className="realm-screen__eyebrow">
            {featuredRoute.familyCode} current · {featuredRoute.stops.length} stops
          </span>
          <strong>{featuredRoute.title}</strong>
          <small>
            {featuredRoute.currentStep} / {featuredRoute.stops.length} restored · tier{' '}
            {featuredRoute.requiredTier}
          </small>
          {featuredRoute.state === 'available' && (
            <SecondaryActionButton
              disabled={!online || Boolean(routePendingId)}
              onClick={() => void beginRoute(featuredRoute)}
            >
              {journey.active ? 'Begin route' : 'Start Journey'}
            </SecondaryActionButton>
          )}
          {featuredRoute.state === 'active' && (
            <span>Next: {featuredRoute.stops.find((stop) => stop.state === 'next')?.name}</span>
          )}
          {featuredRoute.state === 'completed' && <span>Route restored</span>}
        </aside>
      )}
      {revealDiscovery && (
        <div className="explore-discovery-reveal" role="status">
          <span aria-hidden="true" className="explore-discovery-reveal__glyph">
            {discoveryGlyph(revealDiscovery.kind)}
          </span>
          <span className="explore-discovery-reveal__copy">
            <small>New discovery revealed</small>
            <strong>{revealDiscovery.title}</strong>
            <span>Fresh pigment marks the Codex trail.</span>
          </span>
        </div>
      )}
      {(journey.error || journey.capabilityNotice || journey.active) && (
        <div className="journey-detail" role="status">
          <strong>
            {journey.safety.mode === 'journey-passive'
              ? `${journey.queuedDiscoveryCount} discoveries queued safely`
              : 'Journey in progress'}
          </strong>
          <span>
            {journey.error ??
              journey.capabilityNotice ??
              (journey.safety.mode === 'journey-passive'
                ? 'Discoveries are saved for later. Return when safely stopped.'
                : 'Your position is live. Reach a stop safely to play.')}
          </span>
          {journey.error && journey.capabilityNotice && <span>{journey.capabilityNotice}</span>}
          {journey.error && journey.active && journey.safety.mode !== 'journey-passive' && (
            <button
              className="journey-detail__cancel"
              disabled={journey.phase === 'stopping'}
              onClick={() => void journey.cancelJourney()}
              type="button"
            >
              Cancel Journey without reward
            </button>
          )}
        </div>
      )}
      {!journey.active && browserLocation.error && !waitingForCurrentLocation && (
        <div className="map-location-error inline-error" role="alert">
          {browserLocation.error}
        </div>
      )}
      {localWorldActive && !journey.active && (
        <div className="explore-local-rule" role="status">
          <strong>Nearby places · reach within {interactionRadius} m</strong>
          <span>Restored encounters refresh later. Try another place while they rest.</span>
        </div>
      )}
      {nearbyError && !browserLocation.error && (
        <div className="map-location-error inline-error" role="alert">
          {nearbyError}
        </div>
      )}
      {viewportWorld.query.error && !nearbyError && !browserLocation.error && (
        <div className="map-location-error inline-error" role="alert">
          Map places could not refresh. The nearby list remains available.
          <button
            type="button"
            disabled={!online || journey.safety.mode === 'journey-passive'}
            onClick={() => void viewportWorld.query.refetch()}
          >
            Retry map places
          </button>
        </div>
      )}
      {command.error && (
        <div className="remote-expedition-error inline-error" role="alert">
          {command.error.message}
        </div>
      )}
      <ul className="discovery-list" aria-label="Discoveries in this map view · ten nearest">
        {nearestListDiscoveries.map((discovery) => (
          <li className="discovery-list__entry" key={discovery.id}>
            <button
              aria-label={`${discovery.title}. ${
                discoveryDistanceM(discovery, displayedPosition) === undefined
                  ? ''
                  : `${discoveryDistanceM(discovery, displayedPosition)} metres away. `
              }${discoveryStatus(discovery, interactionRadius)}`}
              aria-pressed={discovery.id === selectedId}
              className="discovery-list__item"
              data-discovery-id={discovery.id}
              data-distance-m={discoveryDistanceM(discovery, displayedPosition)}
              data-rarity={discovery.rarity}
              data-state={discovery.state}
              data-testid="seeded-discovery"
              disabled={journey.safety.mode === 'journey-passive'}
              onClick={() => onDiscoverySelected(discovery.id)}
              type="button"
            >
              <WorldNodeArt
                kind={discovery.nodeKind}
                state={discovery.state}
                decorative
                className="discovery-list__mark explore-nearby-art"
              />
              <span className="discovery-list__copy">
                <strong>{discovery.title}</strong>
                <small>
                  {discoveryDistanceM(discovery, displayedPosition) === undefined
                    ? ''
                    : `${discoveryDistanceM(discovery, displayedPosition)} m · `}
                  {discoveryStatus(discovery, interactionRadius)}
                </small>
              </span>
            </button>
          </li>
        ))}
      </ul>
      {selected &&
        selectedDetailsOpen &&
        journey.safety.mode !== 'journey-passive' &&
        createPortal(
          <div className="stop-overlay stop-overlay--illustrated">
            <Modal
              open
              title={
                /^Public rest point \d+$/.test(selected.title) ? 'Wayside rest' : selected.title
              }
              onClose={closeStopDetails}
            >
              <div
                className="discovery-sheet"
                data-new-discovery={selected.id === revealDiscovery?.id || undefined}
              >
                <section
                  className="ar-bottom-sheet ar-sheet explore-stop-card"
                  aria-label="Stop activity"
                >
                  <div aria-hidden="true" className="discovery-sheet__illustration">
                    <WorldNodeArt
                      kind={selected.nodeKind}
                      state={selected.state}
                      decorative
                      className="explore-stop-hero"
                    />
                  </div>
                  <span className="realm-screen__eyebrow">
                    {selected.id === revealDiscovery?.id ? 'New · ' : ''}
                    {selected.nodeKind ? worldNodeLabel(selected.nodeKind) : 'World discovery'}
                    {selected.nodeTier ? ` · Tier ${selected.nodeTier}` : ''}
                  </span>
                  <h3 className="explore-stop-card__activity">
                    {selected.activityTitle || 'Restore this glyph'}
                  </h3>
                  <div className="explore-stop-card__metadata">
                    <span>{selected.rarity}</span>
                    {selectedDistanceM !== undefined && <span>{selectedDistanceM} m away</span>}
                    <span>{selected.visualFamily} family</span>
                  </div>
                  {(selected.state === 'eligible' ||
                    selected.state === 'nearby' ||
                    selected.state === 'queued' ||
                    selected.state === 'cooldown' ||
                    (selected.state === 'claimed' && !selected.attunementLevel)) && (
                    <section className="discovery-sheet__play" data-state={selected.state}>
                      {selected.state === 'eligible' && (
                        <>
                          <span>
                            <strong>Ready to play</strong>
                            <small>Solve the glyph to discover what it holds.</small>
                          </span>
                          <PrimaryActionButton
                            aria-label="Play glyph challenge and secure for Decode"
                            disabled={!online || command.isPending}
                            loading={command.isPending}
                            onClick={claimSelected}
                          >
                            Begin challenge
                          </PrimaryActionButton>
                        </>
                      )}
                      {(selected.state === 'nearby' || selected.state === 'queued') && (
                        <>
                          <span>
                            <strong>
                              {selectedDistanceM !== undefined &&
                              selectedDistanceM > interactionRadius
                                ? `Walk within ${interactionRadius} m`
                                : stopCheck.pending
                                  ? 'Finding you…'
                                  : !online
                                    ? 'Reconnect to unlock'
                                    : !displayedPosition
                                      ? 'Location needed'
                                      : stopCheck.outcome === 'interrupted' || stopCheck.delayed
                                        ? 'Location interrupted'
                                        : 'Checking your location…'}
                            </strong>
                            <small>
                              {selectedDistanceM === undefined ? '' : `${selectedDistanceM} m away`}
                            </small>
                          </span>
                          {journey.error && (!journey.active || journey.phase === 'recovery') && (
                            <PrimaryActionButton
                              disabled={!online || journey.phase === 'starting'}
                              loading={journey.phase === 'starting'}
                              onClick={() => {
                                void journey.beginJourney();
                              }}
                            >
                              Reconnect
                            </PrimaryActionButton>
                          )}
                          {!displayedPosition && !journey.error && (
                            <SecondaryActionButton
                              disabled={!online || browserLocation.phase === 'locating'}
                              onClick={() => void requestBrowserLocation()}
                            >
                              Use my location
                            </SecondaryActionButton>
                          )}
                          {(stopCheck.outcome === 'interrupted' || stopCheck.delayed) &&
                            journey.phase === 'recording' && (
                              <SecondaryActionButton
                                disabled={!stopCheck.canRetry}
                                loading={stopCheck.pending}
                                onClick={() => void stopCheck.check()}
                              >
                                Try location again
                              </SecondaryActionButton>
                            )}
                          <small className="discovery-sheet__play-note">
                            {journey.error
                              ? 'Location interrupted. Reconnect to continue.'
                              : selectedDistanceM !== undefined &&
                                  selectedDistanceM > interactionRadius
                                ? 'Stop safely near this place to open its challenge.'
                                : 'Your challenge opens after your location is confirmed.'}
                          </small>
                        </>
                      )}
                      {selected.state === 'claimed' && !selected.attunementLevel && (
                        <>
                          <span>
                            <strong>Challenge waiting</strong>
                            <small>Your unfinished glyph is ready to continue.</small>
                          </span>
                          <PrimaryActionButton
                            onClick={() => navigate(guidedPath('/decode', guideActive))}
                          >
                            Resume Decode
                          </PrimaryActionButton>
                        </>
                      )}
                      {selected.state === 'cooldown' && (
                        <span>
                          <strong>Stop resting</strong>
                          <small>Come back later, or try another stop nearby.</small>
                        </span>
                      )}
                    </section>
                  )}
                  {selected.state === 'unknown' && (
                    <section className="discovery-sheet__play" data-state="unknown">
                      <span>
                        <strong>A place still to discover</strong>
                        <small>
                          Visit safely within {interactionRadius} m to reveal its challenge.
                        </small>
                      </span>
                    </section>
                  )}
                  {Boolean(selected.attunementLevel) && (
                    <span className="discovery-sheet__attunement">
                      Restored · level {selected.attunementLevel}
                      {selected.weaveRole === 'origin'
                        ? ' · Weave origin'
                        : selected.weaveRole === 'destination'
                          ? ' · Planned destination'
                          : selected.weaveRole === 'woven'
                            ? ' · Current awakened'
                            : ''}
                    </span>
                  )}
                  {activeWeavePlan && Boolean(selected.attunementLevel) && (
                    <SecondaryActionButton
                      onClick={() => {
                        closeStopDetails();
                        setMobilePanel('weaves');
                      }}
                    >
                      View your planned Weave
                    </SecondaryActionButton>
                  )}
                  {selected.state === 'cooldown' && !selected.attunementLevel && (
                    <PrimaryActionButton
                      onClick={() => {
                        closeStopDetails();
                        setMobilePanel('nearby');
                      }}
                    >
                      Find another stop
                    </PrimaryActionButton>
                  )}
                  {!activeWeavePlan &&
                    !weaveOriginId &&
                    Boolean(selected.worldNodeId && selected.attunementLevel) && (
                      <SecondaryActionButton
                        className={
                          selected.state === 'eligible'
                            ? undefined
                            : 'explore-stop-card__next-action'
                        }
                        disabled={weavePending || !online}
                        onClick={() => void chooseWeaveOrigin(selected)}
                      >
                        Connect to another stop
                      </SecondaryActionButton>
                    )}
                  {weaveOriginId && selected.worldNodeId === weaveOriginId && (
                    <SecondaryActionButton
                      onClick={() => {
                        closeStopDetails();
                        setMobilePanel('weaves');
                      }}
                    >
                      Choose a destination
                    </SecondaryActionButton>
                  )}
                  {weaveOriginId &&
                    selected.worldNodeId &&
                    selected.worldNodeId !== weaveOriginId &&
                    selectedWeaveTarget &&
                    selectedWeaveTarget.state !== 'woven' && (
                      <SecondaryActionButton
                        className={
                          selected.state === 'eligible'
                            ? undefined
                            : 'explore-stop-card__next-action'
                        }
                        disabled={weavePending || !online}
                        loading={weavePending}
                        onClick={() => void planSelectedWeave()}
                      >
                        Plan Weave · {selectedWeaveTarget.distanceM} m
                      </SecondaryActionButton>
                    )}
                  {weaveOriginId &&
                    selected.worldNodeId &&
                    selected.worldNodeId !== weaveOriginId &&
                    !selectedWeaveTarget && (
                      <p className="discovery-sheet__range-lock">
                        Visit this stop first, or choose an available destination in Weave.
                      </p>
                    )}
                  {selected.activityTitle && (
                    <section className="discovery-sheet__activity">
                      <details className="discovery-sheet__activity-details">
                        <summary>How to play</summary>
                        <p>{selected.activityObjective}</p>
                        {Boolean(selected.rewardPreview?.length) && (
                          <ul aria-label="Activity outcomes">
                            {selected.rewardPreview?.map((reward) => (
                              <li key={reward}>{reward.replaceAll('_', ' ')}</li>
                            ))}
                          </ul>
                        )}
                      </details>
                    </section>
                  )}
                  {!selected.activityTitle && (
                    <details className="discovery-sheet__activity-details">
                      <summary>About this discovery</summary>
                      <p>{selected.description}</p>
                    </details>
                  )}
                  {command.error && (
                    <span className="inline-error" role="alert">
                      Your challenge could not open. Reconnect and try again.
                    </span>
                  )}
                  {weaveError && (
                    <span className="inline-error" role="alert">
                      {weaveError}
                    </span>
                  )}
                  {selected.worldNodeId && (
                    <StopReport
                      key={selected.worldNodeId}
                      nodeId={selected.worldNodeId}
                      online={online}
                      safetyLocked={stopReportSafetyLocked}
                    />
                  )}
                </section>
              </div>
            </Modal>
          </div>,
          document.body,
        )}
      {!online && (
        <div className="world-state-banner">
          <OfflineState />
        </div>
      )}
    </section>
  );
}

export function ExploreScreen() {
  const query = useVerticalSliceQuery();
  const state = (
    <QueryState
      error={query.error}
      onRetry={() => void query.refetch()}
      pending={query.isPending}
    />
  );
  return query.data ? <ExploreExperience snapshot={query.data} /> : state;
}

const tutorialConceptCopy = {
  wake_source: {
    title: 'Wake the inner channel',
    body: 'Tap the glowing inner stone once. Watch its carved groove make one 90° clockwise turn and open toward the outer ring.',
  },
  carry_energy: {
    title: 'Carry light across the seam',
    body: 'Tap the highlighted outer stone once. The groove makes a 90° turn, catches the jade light and stops short of the seal.',
  },
  reach_seal: {
    title: 'Open the terminal seal',
    body: 'Turn that same outer stone 90° clockwise once more. Its groove now meets the neighboring gold seal and completes the path.',
  },
} as const;

const decodeDifficultyCopy = {
  tutorial: 'A taught two-ring route with no move pressure.',
  common: 'Recognition: one weaving route, dead ends and a fixed stone anchor.',
  advanced: 'Reasoning: two required routes, typed channels, anchors and a tighter move reserve.',
  mastery: 'Mastery: layered routes plus sequence, resonance and obscured-information constraints.',
} as const;

export function DecodeExperience({ snapshot }: { snapshot: PlayerVerticalSliceView }) {
  const navigate = useNavigate();
  const location = useLocation();
  const settings = usePresentationSettings();
  const online = useNetworkStatus();
  const journey = useJourneySafety();
  const command = useVerticalSliceCommand();
  const tutorialSteps = useMemo(
    () => snapshot.decode.puzzle.tutorialSteps ?? [],
    [snapshot.decode.puzzle.tutorialSteps],
  );
  const solved = snapshot.decode.status === 'solved';
  const restoration = usePersonalRestoration(
    snapshot.profile.userId,
    snapshot.source === 'api' && online,
  );
  const savedGoal = restoration.data?.goal;
  const goalReturn = savedGoal ? personalGoalPath(savedGoal) : '/home?panel=selected';
  const personalMaterials = snapshot.decode.rewardMode === 'personal_materials';
  const weaveOnly = snapshot.decode.rewardMode === 'weave_only';
  const civicOnly = snapshot.decode.rewardMode === 'civic_only';
  const personalRewards = !weaveOnly && !civicOnly;
  const weaveAwakened = weaveOnly && solved && snapshot.decode.weaveAwakened === true;
  async function returnToWeaves() {
    await command.mutateAsync({
      type: 'decode.weave.leave',
      commandId: commandId('decode.weave.leave'),
    });
    navigate('/explore?panel=weaves');
  }
  const isGuidedTutorial =
    !solved && snapshot.decode.profileCode === 'tutorial' && tutorialSteps.length > 0;
  const [moves, setMoves] = useState<DecodeMoveView[]>(() =>
    readDecodeDraft(snapshot.profile.userId, snapshot.decode),
  );
  const [rotations, setRotations] = useState(() => draftRotations(snapshot.decode, moves));
  useEffect(() => {
    saveDecodeDraft(snapshot.profile.userId, snapshot.decode, moves);
  }, [snapshot.profile.userId, snapshot.decode, moves]);
  const [canvasReady, setCanvasReady] = useState(false);
  const [segmentIndexExpanded, setSegmentIndexExpanded] = useState(false);
  const [tutorialFeedback, setTutorialFeedback] = useState('');
  const [profileFeedback, setProfileFeedback] = useState<string>(
    decodeDifficultyCopy[snapshot.decode.profileCode],
  );
  const startedAt = useRef<number | null>(null);
  const tutorialStepRef = useRef(moves.length);
  const [activeSegmentId, setActiveSegmentId] = useState(
    tutorialSteps[moves.length]?.segmentId ??
      snapshot.decode.puzzle.segments.find((segment) => !segment.locked)?.id ??
      '',
  );

  const hasVisibleTargets = snapshot.decode.puzzle.segments.every(
    (segment) => segment.targetRotation !== undefined,
  );
  const topology = useMemo(
    () => deriveDecodeVisualState(snapshot.decode.puzzle, rotations),
    [rotations, snapshot.decode.puzzle],
  );
  const reachedTerminalCount = snapshot.decode.puzzle.terminalSegmentIds.filter(
    (segmentId) => topology.segments[segmentId]?.terminalReached,
  ).length;
  const terminalsConnected =
    snapshot.decode.puzzle.terminalSegmentIds.length > 0 &&
    reachedTerminalCount === snapshot.decode.puzzle.terminalSegmentIds.length;
  const moveCount = snapshot.decode.currentMoves + moves.length;
  const rotationSignature = snapshot.decode.puzzle.segments
    .map((segment) => `${segment.id}:${rotations[segment.id] ?? segment.rotation}`)
    .join('|');
  const tutorialStepIndex = Math.min(moves.length, tutorialSteps.length);
  const tutorialExpectedStep = tutorialSteps[tutorialStepIndex];
  const tutorialComplete = isGuidedTutorial && tutorialStepIndex === tutorialSteps.length;
  const readyForVerification = isGuidedTutorial
    ? tutorialComplete
    : terminalsConnected && moves.length > 0;
  const fixture = visualFixtureName();
  const orderedProfiles = useMemo(() => {
    const order = ['tutorial', 'common', 'advanced', 'mastery'] as const;
    return [...snapshot.decodeProfiles].sort(
      (left, right) => order.indexOf(left.code) - order.indexOf(right.code),
    );
  }, [snapshot.decodeProfiles]);
  const guideActive = guidedLoopActive(location.search);
  const completionAction = decodeCompletionAction(
    snapshot.decode,
    guidedPath('/', guideActive),
    savedGoal ? { label: personalGoalLabel(savedGoal), path: goalReturn } : undefined,
  );
  function continueAfterDecode() {
    if (completionAction.kind === 'weave') void returnToWeaves().catch(() => undefined);
    else navigate(completionAction.path);
  }
  const visualState =
    fixture === 'tutorial'
      ? 'decode-tutorial'
      : fixture === 'mid-game'
        ? 'decode-mid-game'
        : fixture === 'solved-reward'
          ? 'decode-solved-reward'
          : undefined;
  useVisualReady(visualState, canvasReady);
  const onCanvasReady = useCallback(() => setCanvasReady(true), [setCanvasReady]);
  const onDecodeVisualEvent = useCallback(
    (event: DecodeVisualEvent) => {
      if (
        event.name === 'segment_rotate' &&
        !settings.reducedMotion &&
        typeof navigator.vibrate === 'function'
      ) {
        navigator.vibrate(8);
      }
    },
    [settings.reducedMotion],
  );

  const rotate = useCallback(
    (segmentId: string, direction: 1 | -1 = 1) => {
      if (solved || journey.safety.mode === 'journey-passive') return;
      const segment = snapshot.decode.puzzle.segments.find((item) => item.id === segmentId);
      if (!segment || segment.locked) return;
      const moveDirection = direction === 1 ? 'clockwise' : 'counter_clockwise';
      if (isGuidedTutorial) {
        const expected = tutorialSteps[tutorialStepRef.current];
        if (!expected) {
          setTutorialFeedback('The path is ready. Awaken the glyph to reveal your discovery.');
          return;
        }
        if (segmentId !== expected.segmentId || moveDirection !== expected.direction) {
          setActiveSegmentId(expected.segmentId);
          setTutorialFeedback(
            `Try the glowing stone and turn it ${expected.direction.replace('_', '-')} as shown. Nothing was changed.`,
          );
          return;
        }
        tutorialStepRef.current += 1;
        const next = tutorialSteps[tutorialStepRef.current];
        setTutorialFeedback(
          next
            ? 'Good—the stone settled and the energy advanced. Follow the next glow.'
            : 'The carved path now connects. Awaken the glyph to reveal your discovery.',
        );
        setActiveSegmentId(next?.segmentId ?? segmentId);
      } else {
        setActiveSegmentId(segmentId);
      }
      setRotations((current) => ({
        ...current,
        [segmentId]: ((current[segmentId] ?? 0) + direction + 4) % 4,
      }));
      setMoves((current) => {
        startedAt.current ??= performance.now();
        const elapsed = Math.max(
          current.at(-1)?.clientElapsedMs ?? 0,
          Math.round(performance.now() - startedAt.current),
        );
        return [
          ...current,
          {
            moveType: 'rotate_segment',
            segmentId,
            direction: moveDirection,
            clientElapsedMs: elapsed,
          },
        ];
      });
    },
    [
      isGuidedTutorial,
      journey.safety.mode,
      snapshot.decode.puzzle.segments,
      solved,
      tutorialSteps,
      setTutorialFeedback,
      setActiveSegmentId,
      setRotations,
      setMoves,
    ],
  );

  const rotateRing = useCallback(
    (ringIndex: number, direction: 1 | -1 = 1) => {
      if (
        solved ||
        journey.safety.mode === 'journey-passive' ||
        !snapshot.decode.puzzle.rotatableRingIndices?.includes(ringIndex)
      )
        return;
      setRotations((current) =>
        Object.fromEntries(
          snapshot.decode.puzzle.segments.map((segment) => [
            segment.id,
            segment.ringIndex === ringIndex
              ? ((current[segment.id] ?? segment.rotation) + direction + 4) % 4
              : (current[segment.id] ?? segment.rotation),
          ]),
        ),
      );
      setMoves((current) => {
        startedAt.current ??= performance.now();
        const elapsed = Math.max(
          current.at(-1)?.clientElapsedMs ?? 0,
          Math.round(performance.now() - startedAt.current),
        );
        return [
          ...current,
          {
            moveType: 'rotate_ring',
            ringIndex,
            direction: direction === 1 ? 'clockwise' : 'counter_clockwise',
            clientElapsedMs: elapsed,
          },
        ];
      });
    },
    [journey.safety.mode, snapshot.decode.puzzle, solved, setRotations, setMoves],
  );

  function handleBoardKeyboard(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'ArrowRight' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      rotate(activeSegmentId, 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      rotate(activeSegmentId, -1);
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (tutorialExpectedStep) {
        setActiveSegmentId(tutorialExpectedStep.segmentId);
        setTutorialFeedback('The glowing stone is selected for this teaching step.');
        return;
      }
      const movable = snapshot.decode.puzzle.segments.filter((segment) => !segment.locked);
      const currentIndex = movable.findIndex((segment) => segment.id === activeSegmentId);
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      const next = movable[(currentIndex + delta + movable.length) % movable.length];
      if (next) setActiveSegmentId(next.id);
    }
  }

  function submitSolution() {
    command.mutate({
      type: 'decode.complete',
      commandId: commandId('decode.complete'),
      moves,
      clientCompletionTimeMs: Math.max(
        100,
        moves.at(-1)?.clientElapsedMs ??
          (startedAt.current === null ? 100 : Math.round(performance.now() - startedAt.current)),
      ),
    });
  }

  function restartPersonalAttempt() {
    if (
      !personalMaterials ||
      solved ||
      !online ||
      command.isPending ||
      journey.safety.mode === 'journey-passive'
    )
      return;
    setMoves([]);
    setRotations(draftRotations(snapshot.decode, []));
    startedAt.current = null;
    saveDecodeDraft(snapshot.profile.userId, snapshot.decode, []);
    command.reset();
  }

  const activeSegmentIndex = snapshot.decode.puzzle.segments.findIndex(
    (segment) => segment.id === activeSegmentId,
  );
  const activeSegment = snapshot.decode.puzzle.segments[activeSegmentIndex];
  const activeSuggestion =
    !isGuidedTutorial && snapshot.decode.suggestedMove?.segmentId === activeSegmentId
      ? snapshot.decode.suggestedMove
      : undefined;
  const activeAligned =
    activeSegment?.targetRotation === undefined
      ? undefined
      : rotations[activeSegment.id] === activeSegment.targetRotation;

  return (
    <section
      className="player-screen decode-screen"
      data-guided={guideActive || undefined}
      data-guided-tutorial={isGuidedTutorial || undefined}
      data-solved={solved || undefined}
      data-visual-ready={visualState && canvasReady ? 'true' : undefined}
      data-visual-state={visualState}
    >
      <DecodeCompletionPrompt
        runId={snapshot.decode.runId}
        ready={readyForVerification}
        verified={solved}
        pending={command.isPending}
        online={online}
        safetyLocked={journey.safety.mode === 'journey-passive'}
        weaveOnly={weaveOnly}
        civicOnly={civicOnly}
        personalMaterials={personalMaterials}
        goalSummary={
          completionAction.kind === 'settlement' && savedGoal
            ? `${personalGoalLabel(savedGoal)} · ${personalGoalSummary(savedGoal)}`
            : undefined
        }
        tutorial={snapshot.decode.profileCode === 'tutorial'}
        rewards={snapshot.decode.rewardPreview}
        error={command.error?.message}
        onVerify={submitSolution}
        onViewBoard={() =>
          document.querySelector('.decode-canvas')?.scrollIntoView?.({ block: 'center' })
        }
        continueLabel={completionAction.label}
        onContinue={continueAfterDecode}
      />
      {guideActive && (
        <GuideCoachmark
          serverCompleted={snapshot.profile.onboardingCompleted}
          serverStep={snapshot.profile.onboardingStep}
          step={solved ? '3 of 4 · Reward' : '2 of 4 · Decode'}
          title={solved ? 'Glyph restored' : 'Restore the energy path'}
        >
          <p>
            {solved
              ? 'Your recovered resources are ready. Build something new or explore another stop.'
              : 'Select a movable stone. Rotate until the carved channels connect the center to every gold seal.'}
          </p>
        </GuideCoachmark>
      )}
      <header className="decode-heading">
        <span className="realm-screen__eyebrow">
          Decode · {snapshot.decode.puzzle.familyLabel} family
        </span>
        <h1>
          {civicOnly
            ? 'Restore a shared chapter'
            : weaveOnly
              ? 'Bind your Knowledge Weave'
              : personalMaterials
                ? 'Restore your Garden chapter'
                : 'Restore the Dawn Path'}
        </h1>
        <p>
          {isGuidedTutorial
            ? 'Learn on a seven-stone mechanism: three guided turns, no timer and no hint cost.'
            : 'Rotate carved grooves to carry jade energy from the center to every gold seal.'}
        </p>
        {civicOnly ? (
          <div className="decode-tutorial-badge" role="note">
            Expedition challenge · hints are free · your personal puzzle is saved
          </div>
        ) : weaveOnly ? (
          <div className="decode-tutorial-badge" role="note">
            Weave binding · no resources, XP or mastery rewards · hints are free
          </div>
        ) : personalMaterials ? (
          <div className="decode-tutorial-badge" role="note">
            Garden restoration · materials for your city · no mastery or XP
          </div>
        ) : isGuidedTutorial ? (
          <div className="decode-tutorial-badge" role="note">
            <span aria-hidden="true">✦</span>
            First restoration · 2 rotating stones · 3 quarter-turns
          </div>
        ) : (
          <>
            <div
              aria-label="Decode difficulty profiles"
              className="decode-profile-strip"
              role="region"
              tabIndex={0}
            >
              {orderedProfiles.map((profile) => {
                const currentProfile = profile.code === snapshot.decode.profileCode;
                const canStartPractice = profile.code === 'tutorial' && !currentProfile;
                const profileState = currentProfile
                  ? 'current'
                  : canStartPractice
                    ? 'practice'
                    : 'explore';
                return (
                  <button
                    aria-pressed={currentProfile}
                    data-profile-code={profile.code}
                    data-state={profileState}
                    disabled={command.isPending || journey.safety.mode === 'journey-passive'}
                    key={profile.code}
                    onClick={() => {
                      if (canStartPractice) {
                        command.mutate({
                          type: 'decode.start',
                          commandId: commandId(`decode.start.${profile.code}`),
                          profileCode: profile.code,
                        });
                        return;
                      }
                      setProfileFeedback(
                        currentProfile
                          ? decodeDifficultyCopy[profile.code]
                          : `Find a ${profile.code} fragment in Explore to unlock a live ${profile.code} restoration. Difficulty cannot be changed on an existing fragment.`,
                      );
                    }}
                    title={
                      currentProfile
                        ? 'Explain this puzzle difficulty'
                        : canStartPractice
                          ? 'Begin a tutorial practice restoration'
                          : `Explain how to unlock ${profile.code}`
                    }
                    type="button"
                  >
                    <strong>{profile.code}</strong>
                    <small>
                      {currentProfile
                        ? 'Current fragment'
                        : canStartPractice
                          ? 'Replay lesson'
                          : 'Find in Explore'}
                    </small>
                  </button>
                );
              })}
            </div>
            <p aria-live="polite" className="decode-profile-feedback">
              {profileFeedback}
            </p>
            <div className="decode-profile-actions">
              <SecondaryActionButton onClick={() => navigate('/explore')}>
                Find a fragment in Explore
              </SecondaryActionButton>
              <SecondaryActionButton onClick={() => navigate('/codex')}>
                View decoded collection
              </SecondaryActionButton>
            </div>
          </>
        )}
      </header>
      <aside className="decode-objective">
        <StonePanel>
          {isGuidedTutorial ? (
            <div aria-label="Guided Decode lesson" className="decode-tutorial-guide" role="region">
              <span className="realm-screen__eyebrow">
                {tutorialComplete
                  ? 'Final step · awaken the glyph'
                  : `Guided turn ${tutorialStepIndex + 1} of ${tutorialSteps.length}`}
              </span>
              <strong>
                {tutorialComplete
                  ? 'The carved path is ready'
                  : tutorialExpectedStep
                    ? tutorialConceptCopy[tutorialExpectedStep.concept].title
                    : 'Follow the illuminated path'}
              </strong>
              <p>
                {tutorialComplete
                  ? 'You made the three teaching turns. The channels connect: awaken the glyph and reveal what you recovered.'
                  : tutorialExpectedStep
                    ? tutorialConceptCopy[tutorialExpectedStep.concept].body
                    : 'Follow the highlighted stone.'}
              </p>
              <ol aria-label="Tutorial energy path" className="decode-tutorial-path">
                {['Center glyph', 'Inner channel', 'Outer channel', 'Gold seal'].map(
                  (label, index) => (
                    <li
                      data-active={
                        !tutorialComplete && Math.min(tutorialStepIndex + 1, 3) === index
                          ? 'true'
                          : undefined
                      }
                      data-complete={tutorialComplete || index <= tutorialStepIndex || undefined}
                      key={label}
                    >
                      <span aria-hidden="true">{index === 0 ? '✦' : index === 3 ? '◆' : '●'}</span>
                      <small>{label}</small>
                    </li>
                  ),
                )}
              </ol>
              <span className="decode-tutorial-feedback">
                {tutorialFeedback ||
                  'Tap the stone with the pulsing turquoise rim. This lesson turns it clockwise; every other tap is safely ignored.'}
              </span>
              {tutorialComplete && !solved && (
                <PrimaryActionButton
                  disabled={!online || command.isPending}
                  loading={command.isPending}
                  onClick={submitSolution}
                >
                  Verify and awaken glyph
                </PrimaryActionButton>
              )}
            </div>
          ) : (
            <>
              <span className="realm-screen__eyebrow">Goal</span>
              <strong>Carry energy to every gold seal</strong>
              <span className="ar-numeric">
                {reachedTerminalCount} / {snapshot.decode.puzzle.terminalSegmentIds.length} seals ·{' '}
                {moveCount} / {snapshot.decode.puzzle.moveLimit ?? '∞'} moves
              </span>
            </>
          )}
        </StonePanel>
      </aside>
      <div
        aria-describedby="decode-keyboard-help"
        className="decode-board"
        data-decode-session-id={snapshot.decode.runId}
        data-reached-terminals={reachedTerminalCount}
        data-required-terminals={snapshot.decode.puzzle.terminalSegmentIds.length}
        data-profile={snapshot.decode.profileCode}
        data-rotation-signature={rotationSignature}
        data-testid="decode-board"
        onKeyDown={handleBoardKeyboard}
        role="application"
        tabIndex={0}
      >
        <span aria-hidden="true" className="decode-board__progress">
          <small>{isGuidedTutorial ? 'Guided lesson' : 'Path progress'}</small>
          <strong className="ar-numeric">
            {isGuidedTutorial
              ? tutorialComplete
                ? 'Ready to verify'
                : `Turn ${tutorialStepIndex + 1} / ${tutorialSteps.length}`
              : `${moveCount} / ${snapshot.decode.puzzle.moveLimit ?? '∞'} moves`}
          </strong>
          {isGuidedTutorial && !tutorialComplete && tutorialExpectedStep && (
            <em>{tutorialConceptCopy[tutorialExpectedStep.concept].title}</em>
          )}
        </span>
        <DecodeCanvas
          onSegmentSelected={rotate}
          onReady={onCanvasReady}
          onVisualEvent={onDecodeVisualEvent}
          puzzle={snapshot.decode.puzzle}
          qualityTier={settings.qualityTier}
          reducedMotion={settings.reducedMotion}
          rotations={rotations}
          selectedSegmentId={activeSegmentId}
          solved={solved}
          suggestedSegmentId={
            isGuidedTutorial
              ? tutorialExpectedStep?.segmentId
              : snapshot.decode.suggestedMove?.segmentId
          }
        />
        <span className="ar-visually-hidden" id="decode-keyboard-help">
          {isGuidedTutorial
            ? 'The lesson selects the glowing stone. Right arrow, Space or Enter makes the shown clockwise turn. Wrong turns are ignored.'
            : 'Use up and down arrows to choose a movable segment. Right arrow, Space or Enter rotates clockwise; left arrow rotates counter-clockwise.'}
        </span>
      </div>
      <aside
        aria-label={solved ? 'Restoration next step' : 'Accessible glyph segment controls'}
        className="decode-controls"
      >
        {solved ? (
          <div className="decode-next-step" role="status">
            <span aria-hidden="true" className="decode-next-step__seal">
              ✦
            </span>
            <span className="realm-screen__eyebrow">
              {civicOnly
                ? 'Expedition glyph restored'
                : weaveOnly
                  ? 'Binding verified'
                  : 'Path restored · reward secured'}
            </span>
            <h2>
              {civicOnly
                ? 'Bring this chapter home'
                : weaveOnly
                  ? weaveAwakened
                    ? 'Knowledge Weave awakened'
                    : 'Binding result'
                  : completionAction.kind === 'world'
                    ? 'Continue exploring'
                    : 'Continue to your settlement'}
            </h2>
            <p>
              {civicOnly
                ? 'Your restoration is ready to contribute. Return to your expedition to advance the shared story.'
                : weaveOnly
                  ? weaveAwakened
                    ? 'Your verified walk and binding have awakened this current. No resources, XP or mastery were awarded.'
                    : 'Return to Explore to check the current Weave state. No resources, XP or mastery were awarded.'
                  : completionAction.kind === 'world'
                    ? 'This stop is restored. Return to Explore to find your next discovery.'
                    : 'This puzzle is complete. Spend the recovered resources on your first building step; you can inspect the unlocked lore afterwards.'}
            </p>
            <PrimaryActionButton disabled={command.isPending} onClick={continueAfterDecode}>
              {completionAction.label}
            </PrimaryActionButton>
            {personalRewards && !personalMaterials && (
              <SecondaryActionButton onClick={() => navigate('/codex')}>
                View unlocked Codex lore
              </SecondaryActionButton>
            )}
          </div>
        ) : (
          <>
            <h2>{isGuidedTutorial ? 'Make the shown turn' : 'Turn the carved path'}</h2>
            <p>
              {isGuidedTutorial
                ? tutorialComplete
                  ? 'The lesson is complete. Verify the recorded turns to awaken the center.'
                  : 'The pulsing rim identifies the stone. Use the single enabled direction below.'
                : 'Tap a stone in the glyph, then turn only that selected segment.'}
            </p>
            {!isGuidedTutorial && (
              <button
                aria-controls="decode-segment-index"
                aria-expanded={segmentIndexExpanded}
                className="segment-index-toggle"
                onClick={() => setSegmentIndexExpanded((expanded) => !expanded)}
                type="button"
              >
                {segmentIndexExpanded ? 'Hide stone index' : 'Choose another stone'}
              </button>
            )}
            {!isGuidedTutorial &&
              (snapshot.decode.puzzle.rotatableRingIndices?.length ?? 0) > 0 && (
                <div aria-label="Whole ring controls" className="decode-ring-controls">
                  <span>
                    <small>Whole-ring tactic</small>
                    <strong>Turn every groove together</strong>
                  </span>
                  {snapshot.decode.puzzle.rotatableRingIndices?.map((ringIndex) => (
                    <div key={ringIndex}>
                      <em>Ring {ringIndex + 1}</em>
                      <button
                        aria-label={`Rotate ring ${ringIndex + 1} counter-clockwise`}
                        disabled={
                          solved ||
                          snapshot.decode.modifiers.includes('directional_channels') ||
                          journey.safety.mode === 'journey-passive'
                        }
                        onClick={() => rotateRing(ringIndex, -1)}
                        type="button"
                      >
                        ↶
                      </button>
                      <button
                        aria-label={`Rotate ring ${ringIndex + 1} clockwise`}
                        disabled={solved || journey.safety.mode === 'journey-passive'}
                        onClick={() => rotateRing(ringIndex, 1)}
                        type="button"
                      >
                        ↷
                      </button>
                    </div>
                  ))}
                </div>
              )}
            <div
              aria-label="Glyph segment selector"
              className="segment-control-list"
              hidden={isGuidedTutorial || !segmentIndexExpanded}
              id="decode-segment-index"
              role="region"
              tabIndex={0}
            >
              {snapshot.decode.puzzle.segments.map((segment, index) => {
                const isAligned =
                  segment.targetRotation === undefined
                    ? undefined
                    : rotations[segment.id] === segment.targetRotation;
                const suggestion = snapshot.decode.suggestedMove;
                const isSuggested = suggestion?.segmentId === segment.id;
                return (
                  <div
                    className="segment-control"
                    data-active={segment.id === activeSegmentId || undefined}
                    data-segment-id={segment.id}
                    data-state={
                      segment.locked
                        ? 'locked'
                        : isSuggested
                          ? 'hinted'
                          : isAligned === true
                            ? 'aligned'
                            : isAligned === false
                              ? 'interrupted'
                              : 'unknown'
                    }
                    data-suggested-direction={isSuggested ? suggestion.direction : undefined}
                    key={segment.id}
                  >
                    <button
                      aria-label={`Select segment ${index + 1}${segment.locked ? ', locked anchor' : ''}`}
                      aria-pressed={segment.id === activeSegmentId}
                      className="segment-control__selector"
                      disabled={
                        solved ||
                        journey.safety.mode === 'journey-passive' ||
                        (isGuidedTutorial && tutorialExpectedStep?.segmentId !== segment.id)
                      }
                      onClick={() => setActiveSegmentId(segment.id)}
                      type="button"
                    >
                      <strong>{index + 1}</strong>
                      <span aria-hidden="true">
                        {segment.locked ? '⌑' : isSuggested ? '✦' : isAligned ? '•' : '—'}
                      </span>
                    </button>
                    <span className="ar-visually-hidden">
                      {segment.locked
                        ? 'Locked anchor'
                        : isSuggested
                          ? `Hint: rotate ${suggestion.direction.replace('_', ' ')}`
                          : isAligned === undefined
                            ? `Rotation ${rotations[segment.id] ?? 0} of 3`
                            : isAligned
                              ? 'Channel aligned'
                              : 'Channel interrupted'}
                    </span>
                  </div>
                );
              })}
            </div>
            {activeSegment && (
              <div className="active-segment-control" data-segment-id={activeSegment.id}>
                <span>
                  <small>Selected stone</small>
                  <strong>
                    {isGuidedTutorial
                      ? tutorialStepIndex === 0
                        ? 'Inner channel'
                        : 'Outer channel'
                      : `Segment ${activeSegmentIndex + 1}`}
                  </strong>
                  <em>
                    {activeSegment.locked
                      ? 'Locked anchor'
                      : isGuidedTutorial
                        ? tutorialComplete
                          ? 'All teaching turns complete'
                          : `Turn ${tutorialExpectedStep?.direction.replace('_', '-') ?? 'as shown'} now`
                        : activeSuggestion
                          ? `Hint: turn ${activeSuggestion.direction.replace('_', ' ')}`
                          : activeAligned === undefined
                            ? `Rotation ${rotations[activeSegment.id] ?? 0} of 3`
                            : activeAligned
                              ? 'Aligned'
                              : 'Interrupted'}
                  </em>
                </span>
                <button
                  aria-label={`Rotate segment ${activeSegmentIndex + 1} counter-clockwise`}
                  disabled={
                    activeSegment.locked ||
                    solved ||
                    journey.safety.mode === 'journey-passive' ||
                    (isGuidedTutorial &&
                      (tutorialComplete ||
                        tutorialExpectedStep?.segmentId !== activeSegment.id ||
                        tutorialExpectedStep?.direction !== 'counter_clockwise'))
                  }
                  onClick={() => rotate(activeSegment.id, -1)}
                  type="button"
                >
                  ↶
                </button>
                <button
                  aria-label={`Rotate segment ${activeSegmentIndex + 1} clockwise`}
                  data-segment-id={activeSegment.id}
                  data-testid="decode-segment"
                  data-tutorial-action={
                    isGuidedTutorial &&
                    !tutorialComplete &&
                    tutorialExpectedStep?.segmentId === activeSegment.id &&
                    tutorialExpectedStep.direction === 'clockwise'
                      ? 'current'
                      : undefined
                  }
                  disabled={
                    activeSegment.locked ||
                    solved ||
                    journey.safety.mode === 'journey-passive' ||
                    (isGuidedTutorial &&
                      (tutorialComplete ||
                        tutorialExpectedStep?.segmentId !== activeSegment.id ||
                        tutorialExpectedStep?.direction !== 'clockwise'))
                  }
                  onClick={() => rotate(activeSegment.id, 1)}
                  type="button"
                >
                  ↷
                </button>
              </div>
            )}
          </>
        )}
      </aside>
      <aside className="decode-reward">
        <StonePanel ornament={2}>
          {personalRewards && (!isGuidedTutorial || solved) && (
            <RewardBundle
              resources={snapshot.decode.rewardPreview}
              title={
                solved
                  ? personalMaterials
                    ? 'Materials recovered'
                    : 'Knowledge recovered'
                  : 'Reward preview'
              }
            />
          )}
          {weaveOnly && (
            <div className="decode-tutorial-outcome">
              <strong>
                {weaveAwakened ? 'Your current is awakened' : 'A binding, not a resource reward'}
              </strong>
              <p>
                This Decode binds only your chosen Weave. Your ordinary puzzle and its rewards
                remain separate.
              </p>
              {!solved && (
                <SecondaryActionButton
                  disabled={command.isPending}
                  onClick={() => void returnToWeaves().catch(() => undefined)}
                >
                  Return to Knowledge Weaves
                </SecondaryActionButton>
              )}
            </div>
          )}
          {civicOnly && (
            <div className="decode-tutorial-outcome">
              <strong>A chapter for your shared city</strong>
              <p>
                Restore this glyph to advance your expedition. This challenge grants no personal
                resources or mastery.
              </p>
              {!solved && (
                <SecondaryActionButton
                  disabled={command.isPending}
                  onClick={() => navigate('/tribe#civic-expedition-title')}
                >
                  Return to your expedition
                </SecondaryActionButton>
              )}
            </div>
          )}
          {isGuidedTutorial && !solved && (
            <div className="decode-tutorial-outcome">
              <span className="realm-screen__eyebrow">What this teaches</span>
              <strong>Follow the carving. Let the light guide you.</strong>
              <p>
                Follow the carved groove, not just the glow. Each press turns one groove by 90°;
                connected jade carries light. Reach every gold seal to restore the glyph.
              </p>
            </div>
          )}
          {!solved && !isGuidedTutorial && (
            <div className="decode-verification-actions">
              {personalMaterials &&
                snapshot.decode.puzzle.moveLimit !== undefined &&
                moveCount >= snapshot.decode.puzzle.moveLimit && (
                  <SecondaryActionButton
                    disabled={
                      !online || command.isPending || journey.safety.mode === 'journey-passive'
                    }
                    onClick={restartPersonalAttempt}
                  >
                    Restart this Garden glyph
                  </SecondaryActionButton>
                )}
              {!hasVisibleTargets && (
                <SecondaryActionButton
                  disabled={
                    !online || command.isPending || journey.safety.mode === 'journey-passive'
                  }
                  onClick={() =>
                    command.mutate({
                      type: 'decode.hint',
                      commandId: commandId('decode.hint'),
                      moves,
                    })
                  }
                >
                  {civicOnly
                    ? 'Reveal a free expedition hint'
                    : weaveOnly
                      ? 'Reveal a free binding hint'
                      : 'Reveal one channel hint'}
                </SecondaryActionButton>
              )}
              <PrimaryActionButton
                disabled={
                  !readyForVerification ||
                  !online ||
                  command.isPending ||
                  journey.safety.mode === 'journey-passive'
                }
                loading={command.isPending}
                onClick={submitSolution}
              >
                {readyForVerification ? 'Verify restoration' : 'Complete the energy path'}
              </PrimaryActionButton>
            </div>
          )}
          {solved && personalRewards && (
            <div className="decode-success" role="status">
              <span aria-hidden="true" className="decode-success__crest">
                ✦
              </span>
              <span className="realm-screen__eyebrow">Restoration complete</span>
              <strong>Glyph restored</strong>
              <p>
                {savedGoal
                  ? personalGoalSummary(savedGoal)
                  : personalMaterials
                    ? 'Your garden materials are ready to use in your city.'
                    : 'Your recovered resources are ready to use. The discovery is saved in your Codex.'}
              </p>
              <div>
                <PrimaryActionButton
                  onClick={() => navigate(guideActive ? '/?guide=1' : goalReturn)}
                >
                  {savedGoal ? `Return to ${personalGoalLabel(savedGoal)}` : 'Use in settlement'}
                </PrimaryActionButton>
                <SecondaryActionButton onClick={() => navigate('/codex')}>
                  View Codex
                </SecondaryActionButton>
              </div>
            </div>
          )}
          {command.error && (
            <span className="inline-error" role="alert">
              {command.error.message}
            </span>
          )}
        </StonePanel>
      </aside>
      {!isGuidedTutorial && (
        <aside className="decode-mastery-summary" aria-label="Current Decode profile details">
          {snapshot.source === 'api' && (
            <PersonalProgress key={snapshot.profile.userId} snapshot={snapshot} surface="decode" />
          )}
          <StonePanel>
            <span className="realm-screen__eyebrow">{snapshot.decode.profileCode} profile</span>
            <h2>Why this is {snapshot.decode.profileCode}</h2>
            <p>{decodeDifficultyCopy[snapshot.decode.profileCode]}</p>
            <dl className="decode-difficulty-facts">
              <div>
                <dt>Rings</dt>
                <dd>{snapshot.decode.puzzle.ringCount}</dd>
              </div>
              <div>
                <dt>Seals</dt>
                <dd>{snapshot.decode.puzzle.terminalSegmentIds.length}</dd>
              </div>
              <div>
                <dt>Par turns</dt>
                <dd>{snapshot.decode.puzzle.parMoves ?? '—'}</dd>
              </div>
              <div>
                <dt>Anchors</dt>
                <dd>
                  {snapshot.decode.puzzle.segments.filter((segment) => segment.locked).length}
                </dd>
              </div>
            </dl>
            <h3>
              {civicOnly
                ? 'Expedition objective'
                : weaveOnly
                  ? 'Binding objective'
                  : personalMaterials
                    ? 'Garden objective'
                    : 'Mastery conditions'}
            </h3>
            <ul>
              {civicOnly ? (
                <li>Restore every required seal for your shared chapter.</li>
              ) : weaveOnly ? (
                <li>Connect every required seal to bind the Weave.</li>
              ) : personalMaterials ? (
                <li>Restore every required seal to earn materials for your city.</li>
              ) : (
                snapshot.decode.masteryTargets.map((target) => (
                  <li key={target}>{target.replaceAll('_', ' ')}</li>
                ))
              )}
            </ul>
            <p>
              {snapshot.decode.modifiers.length > 0
                ? `Modifiers: ${snapshot.decode.modifiers.join(', ').replaceAll('_', ' ')}`
                : 'No additional modifiers.'}
            </p>
          </StonePanel>
          <GuideEntry compact returnTo="/decode" />
        </aside>
      )}
      <div aria-live="polite" className="ar-visually-hidden">
        {isGuidedTutorial && !solved
          ? tutorialComplete
            ? 'All three tutorial turns are complete. Awaken your glyph.'
            : `Tutorial turn ${tutorialStepIndex + 1} of ${tutorialSteps.length}. ${tutorialConceptCopy[tutorialExpectedStep?.concept ?? 'wake_source'].title}.`
          : terminalsConnected && !solved
            ? 'Every gold seal is connected. Your glyph is ready to awaken.'
            : snapshot.decode.suggestedMove
              ? `Hint available for ${snapshot.decode.suggestedMove.segmentId}.`
              : solved
                ? civicOnly
                  ? 'Expedition glyph restored. Return to your expedition to contribute.'
                  : weaveOnly
                    ? weaveAwakened
                      ? 'Knowledge Weave awakened. No resource rewards were granted.'
                      : 'Binding verified. Check the Weave state in Explore.'
                    : 'The glyph is restored and rewards are available.'
                : `${moveCount} moves used.`}
      </div>
      {!online && (
        <div className="world-state-banner">
          <OfflineState />
        </div>
      )}
    </section>
  );
}

export function DecodeScreen() {
  const query = useVerticalSliceQuery();
  const dailyEntry = useDailyDecodeEntryState(query.data);
  const dailyMessage = dailyEntry.message;
  useSceneStartup(
    'decode',
    false,
    dailyEntry.error,
    dailyEntry.pending || Boolean(dailyEntry.error),
  );
  const state = (
    <QueryState
      error={query.error}
      onRetry={() => void query.refetch()}
      pending={query.isPending}
    />
  );
  // Never mount the previous solved renderer while the server chooses today's board.
  return query.data && dailyEntry.error ? (
    <StatePanel
      kind="error"
      title="Your Daily Codex could not open"
      actionLabel="Try again"
      onAction={() => window.location.reload()}
    >
      Try again to continue. Your puzzle progress is kept.
    </StatePanel>
  ) : query.data && dailyEntry.pending ? (
    <LoadingState title="Preparing your Daily Codex" />
  ) : query.data ? (
    <>
      {dailyMessage && (
        <p className="decode-daily-status" role="status">
          {dailyMessage}
        </p>
      )}
      <DecodeExperience key={query.data.decode.runId} snapshot={query.data} />
    </>
  ) : (
    state
  );
}

function CodexEntryDetail({ entry, family }: { entry: CodexEntryView; family: CodexFamilyView }) {
  const illustration = codexIllustration(entry);
  return (
    <ParchmentPanel className="codex-detail" ornament={2}>
      <span className="ar-visually-hidden">{entry.reviewStatus.replaceAll('_', ' ')}</span>
      <figure
        aria-hidden="true"
        className="codex-detail__plate"
        data-illustrated={Boolean(illustration)}
        data-restored={codexRestored(entry)}
      >
        {illustration ? (
          <img alt="" src={illustration.url} />
        ) : (
          <span className="codex-detail__glyph">
            <CodexGlyph entry={entry} family={family} />
          </span>
        )}
        <span className="codex-detail__seal">
          {codexRestored(entry) ? `${family.label} · restored` : 'Unrestored sign'}
        </span>
      </figure>
      <span className="realm-screen__eyebrow">{entry.status} · living chronicle</span>
      <h2>{entry.title ?? 'Undecoded sign'}</h2>
      <p>
        {entry.lorePreview ??
          'Its silhouette is recorded, but its meaning remains beyond the known path.'}
      </p>
      <dl>
        <div>
          <dt>Family</dt>
          <dd>{family.label}</dd>
        </div>
        <div>
          <dt>First seen</dt>
          <dd>
            {entry.foundAt ??
              (entry.status === 'decoded' || entry.status === 'mastered'
                ? 'Recorded in your archive'
                : 'Not yet discovered')}
          </dd>
        </div>
        <div>
          <dt>Collection</dt>
          <dd>
            {family.decodedCount} of {family.totalCount} signs restored
          </dd>
        </div>
      </dl>
      <div className="codex-discovery-celebration" data-mastered={entry.status === 'mastered'}>
        <span aria-hidden="true">✦</span>
        <div>
          <strong>
            {entry.status === 'mastered'
              ? 'Mastery illuminated'
              : entry.status === 'decoded'
                ? 'Archive restored'
                : 'Awaiting restoration'}
          </strong>
          <small>
            {entry.status === 'decoded' || entry.status === 'mastered'
              ? 'Knowledge now shapes your settlement paths.'
              : 'Find and Decode this sign to reveal its story.'}
          </small>
        </div>
      </div>
    </ParchmentPanel>
  );
}

function CodexExperience({ snapshot }: { snapshot: PlayerVerticalSliceView }) {
  const navigate = useNavigate();
  const visualState = visualFixtureName() === 'collection' ? 'codex-collection' : undefined;
  useVisualReady(visualState, true);
  const initialFamily =
    snapshot.codexFamilies.find((item) =>
      item.entries.some((entry) => entry.status === 'decoded' || entry.status === 'mastered'),
    ) ?? snapshot.codexFamilies[0];
  const [familyId, setFamilyId] = useState(initialFamily?.id);
  const family =
    snapshot.codexFamilies.find((item) => item.id === familyId) ?? snapshot.codexFamilies[0];
  const initialEntry =
    family?.entries.find((item) => item.status === 'decoded' || item.status === 'mastered') ??
    family?.entries[0];
  const [entryId, setEntryId] = useState(initialEntry?.id);
  const [detailOpen, setDetailOpen] = useState(false);
  const closeDetail = useCallback(() => setDetailOpen(false), []);
  const entry = family?.entries.find((item) => item.id === entryId) ?? family?.entries[0];

  if (!family || !entry) {
    return (
      <section className="screen-state">
        <StatePanel kind="empty" title="The Codex awaits its first sign">
          Secure and Decode a fragment to begin this living archive.
        </StatePanel>
      </section>
    );
  }

  return (
    <section
      className="player-screen codex-screen"
      data-visual-ready={visualState ? 'true' : undefined}
      data-visual-state={visualState}
    >
      <header className="codex-heading">
        <span className="codex-heading__mark" aria-hidden="true">
          <GlyphIcon surface="codex" />
        </span>
        <div>
          <span className="realm-screen__eyebrow">Knowledge endures</span>
          <h1>Codex</h1>
        </div>
        <p>Discover · collect · understand</p>
      </header>
      <nav aria-label="Glyph families" className="codex-families">
        {snapshot.codexFamilies.map((item) => (
          <button
            aria-current={item.id === family.id ? 'page' : undefined}
            key={item.id}
            onClick={() => {
              setFamilyId(item.id);
              setEntryId(
                item.entries.find(
                  (entry) => entry.status === 'decoded' || entry.status === 'mastered',
                )?.id ?? item.entries[0]?.id,
              );
            }}
            type="button"
          >
            <span aria-hidden="true">
              <CodexGlyph entry={item.entries[0] ?? entry} family={item} familyOnly />
            </span>
            <strong>{item.label}</strong>
            <small>
              {item.decodedCount}/{item.totalCount}
            </small>
          </button>
        ))}
      </nav>
      <section aria-labelledby="codex-grid-title" className="codex-grid-panel">
        <header>
          <div>
            <span className="realm-screen__eyebrow">Glyph family</span>
            <h2 id="codex-grid-title">{family.label}</h2>
          </div>
          <span>
            {family.decodedCount} / {family.totalCount} decoded
          </span>
        </header>
        <div className="codex-grid">
          {family.entries.map((item) => (
            <button
              aria-pressed={item.id === entry.id}
              className="codex-card"
              data-entry-code={item.id}
              data-status={item.status}
              data-testid="codex-entry"
              key={item.id}
              onClick={() => {
                setEntryId(item.id);
                if (window.matchMedia('(max-width: 759px)').matches) setDetailOpen(true);
              }}
              type="button"
            >
              <span aria-hidden="true" className="codex-card__glyph">
                <CodexGlyph entry={item} family={family} />
              </span>
              <strong>{item.title ?? 'Unknown sign'}</strong>
              <small>{item.status}</small>
            </button>
          ))}
        </div>
        <div role="group" aria-label="Recent discoveries" className="codex-recent-discoveries">
          <span className="realm-screen__eyebrow">Recent discoveries</span>
          {family.entries
            .filter((item) => item.status !== 'unknown')
            .slice(0, 3)
            .map((item, index) => (
              <div data-status={item.status} key={item.id}>
                <span aria-hidden="true">
                  <CodexGlyph entry={item} family={family} />
                </span>
                <small>{item.title ?? `Archive find ${index + 1}`}</small>
              </div>
            ))}
        </div>
      </section>
      <div className="codex-detail-slot">
        <CodexEntryDetail entry={entry} family={family} />
      </div>
      {createPortal(
        <div className="codex-mobile-reader">
          <Modal open={detailOpen} onClose={closeDetail} title={entry.title ?? 'Undecoded sign'}>
            <CodexEntryDetail entry={entry} family={family} />
          </Modal>
        </div>,
        document.body,
      )}
      <aside className="mastery-rail">
        <StonePanel ornament={2}>
          <span className="realm-screen__eyebrow">Mastery</span>
          <div className="codex-mastery-heading">
            <span aria-hidden="true">
              <CodexGlyph entry={entry} family={family} familyOnly />
            </span>
            <div>
              <h2>{family.label} family</h2>
              <p>{family.masteryTier}</p>
            </div>
          </div>
          <ProgressMeter
            label={`${family.label} family mastery`}
            max={family.totalCount}
            value={family.decodedCount}
          />
          <h3 className="codex-mastery-caption">Your restoration practice</h3>
          <ol aria-label="Restoration practice">
            {[...snapshot.decodeProfiles]
              .sort((left, right) => codexProfileOrder(left.code) - codexProfileOrder(right.code))
              .map((profile) => {
                const mastery = snapshot.decodeMastery.find(
                  (item) => item.familyCode === family.id && item.profileCode === profile.code,
                );
                return (
                  <li
                    data-profile={profile.code}
                    data-active={Boolean(mastery?.completions) || undefined}
                    key={profile.code}
                  >
                    <span aria-hidden="true">{mastery?.completions ? '✓' : '◇'}</span>
                    <div>
                      <strong>{codexProfileLabel(profile.code)}</strong>
                      <small>
                        {mastery
                          ? `${mastery.completions} restored · ${mastery.masteryPoints} points · best ${mastery.bestScore}`
                          : profile.masteryTargets.map(codexTargetLabel).join(' · ')}
                      </small>
                    </div>
                  </li>
                );
              })}
          </ol>
        </StonePanel>
      </aside>
      <section className="solo-paths" aria-labelledby="solo-paths-title">
        <header>
          <span className="realm-screen__eyebrow">Solo progression</span>
          <h2 id="solo-paths-title">Three independent routes</h2>
          <p>Every endgame settlement tier remains reachable without Tribe membership.</p>
        </header>
        <div>
          {snapshot.soloPaths.map((path, index) => (
            <JadeGlassPanel className="solo-path" key={path.id}>
              <span className="solo-path__number">0{index + 1}</span>
              <h3>{path.label}</h3>
              <p>{path.description}</p>
              <ProgressMeter label={`${path.label} progress`} value={path.progress} />
              <small>Next · {path.nextMilestone}</small>
            </JadeGlassPanel>
          ))}
        </div>
        <SecondaryActionButton onClick={() => navigate('/solo')}>
          Open live solo progression
        </SecondaryActionButton>
      </section>
      <CodexConstellations enabled={snapshot.source === 'api'} />
    </section>
  );
}

export function CodexScreen() {
  const query = useVerticalSliceQuery();
  const state = (
    <QueryState
      error={query.error}
      onRetry={() => void query.refetch()}
      pending={query.isPending}
    />
  );
  return query.data ? <CodexExperience snapshot={query.data} /> : state;
}

function SoloExperience({ snapshot }: { snapshot: PlayerVerticalSliceView }) {
  const navigate = useNavigate();
  const online = useNetworkStatus();
  const journey = useJourneySafety();
  const command = useVerticalSliceCommand();
  const visualState = visualFixtureName() === 'advanced' ? 'solo-progression' : undefined;
  useVisualReady(visualState, true);
  const locked = !online || journey.safety.mode === 'journey-passive';

  return (
    <section
      className="player-screen solo-screen"
      data-independent-of-tribe={snapshot.solo.independentOfTribe}
      data-visual-ready={visualState ? 'true' : undefined}
      data-visual-state={visualState}
    >
      <header className="solo-heading">
        <div className="solo-heading__copy">
          <span className="realm-screen__eyebrow">Codex annex · solo progression</span>
          <h1>Your own road to the horizon</h1>
          <p>
            Ranks, contracts and merchant orders are server-authorized and never require Tribe
            membership.
          </p>
        </div>
        <div className="solo-heading__actions">
          <strong className="solo-current-rank">
            Current rank · {snapshot.solo.currentRank.replaceAll('_', ' ')}
          </strong>
          <SecondaryActionButton onClick={() => navigate('/codex')}>
            Return to the Codex
          </SecondaryActionButton>
        </div>
      </header>

      <section aria-labelledby="solo-ranks-title" className="solo-ranks">
        <h2 id="solo-ranks-title">Rank path</h2>
        <ol>
          {snapshot.solo.ranks.map((rank) => (
            <li data-eligible={rank.eligible || undefined} key={rank.code}>
              <span className="solo-rank-ordinal">{String(rank.ordinal + 1).padStart(2, '0')}</span>
              <div>
                <strong>{rank.displayName}</strong>
                {Object.entries(rank.requirements).map(([requirement, target]) => (
                  <small key={requirement}>
                    {requirement.replaceAll('_', ' ')} · {rank.progress[requirement] ?? 0}/{target}
                  </small>
                ))}
              </div>
              <span>{rank.eligible ? 'Eligible' : 'In progress'}</span>
            </li>
          ))}
        </ol>
      </section>

      <section aria-labelledby="solo-contracts-title" className="solo-contracts">
        <header>
          <span className="realm-screen__eyebrow">Rotating contracts</span>
          <h2 id="solo-contracts-title">Craft for the next milestone</h2>
        </header>
        <div className="solo-card-grid">
          {snapshot.solo.contracts.map((contract) => (
            <StonePanel className="solo-live-card" key={contract.id}>
              <span className="realm-screen__eyebrow">{contract.specialization}</span>
              <h3>{contract.code.replaceAll('_', ' ')}</h3>
              <ProgressMeter
                label={`${contract.code} progress`}
                max={contract.target}
                value={contract.current}
              />
              <RewardBundle resources={contract.rewards} />
              <PrimaryActionButton
                disabled={locked || command.isPending || contract.status !== 'completed'}
                loading={command.isPending}
                onClick={() =>
                  command.mutate({
                    type: 'solo.contract.claim',
                    commandId: commandId(`solo.contract.${contract.id}`),
                    contractId: contract.id,
                  })
                }
              >
                {contract.status === 'claimed' ? 'Reward claimed' : 'Claim contract reward'}
              </PrimaryActionButton>
            </StonePanel>
          ))}
          {snapshot.solo.contracts.length === 0 && (
            <StatePanel kind="empty" title="New contracts are being prepared">
              Your rank path remains available while the next rotation is assembled.
            </StatePanel>
          )}
        </div>
      </section>

      <section aria-labelledby="merchant-orders-title" className="merchant-orders">
        <header>
          <span className="realm-screen__eyebrow">Merchant route</span>
          <h2 id="merchant-orders-title">Independent orders</h2>
        </header>
        <div className="solo-card-grid">
          {snapshot.solo.merchantOrders.map((order) => (
            <JadeGlassPanel className="solo-live-card" key={order.id}>
              <h3>{order.code.replaceAll('_', ' ')}</h3>
              <ProgressMeter
                label={`${order.code} fulfilment`}
                max={order.targetQuantity}
                value={order.fulfilledQuantity}
              />
              <p>
                One parcel costs {order.unitCost} {order.resourceCode}.
              </p>
              <RewardBundle resources={order.completionRewards} />
              <PrimaryActionButton
                disabled={
                  locked ||
                  command.isPending ||
                  order.status === 'completed' ||
                  order.fulfilledQuantity >= order.targetQuantity
                }
                loading={command.isPending}
                onClick={() =>
                  command.mutate({
                    type: 'solo.order.fulfill',
                    commandId: commandId(`solo.order.${order.id}.${order.fulfilledQuantity}`),
                    orderId: order.id,
                    quantity: 1,
                  })
                }
              >
                Fulfil one parcel
              </PrimaryActionButton>
            </JadeGlassPanel>
          ))}
        </div>
      </section>
      {command.error && (
        <span className="inline-error solo-error" role="alert">
          {command.error.message}
        </span>
      )}
    </section>
  );
}

export function SoloScreen() {
  const query = useVerticalSliceQuery();
  const state = (
    <QueryState
      error={query.error}
      onRetry={() => void query.refetch()}
      pending={query.isPending}
    />
  );
  return query.data ? <SoloExperience snapshot={query.data} /> : state;
}

export function TribeScreen() {
  const query = useVerticalSliceQuery();
  const state = (
    <QueryState
      error={query.error}
      onRetry={() => void query.refetch()}
      pending={query.isPending}
    />
  );
  return query.data ? <TribeExperience snapshot={query.data} /> : state;
}

function feedLabel(
  eventType: PlayerVerticalSliceView['tribe']['feed'][number]['eventType'],
  payload: Readonly<Record<string, string | number | boolean>>,
): string {
  if (eventType === 'project_contributed') {
    return `Contributed ${String(payload.amount ?? '')} ${String(payload.resource_code ?? 'resources')}`;
  }
  if (eventType === 'project_phase_completed') {
    return `Completed civic phase ${String(payload.phase ?? '')}`;
  }
  if (eventType === 'market_trade_completed') {
    return `Exchanged ${String(payload.offered_amount ?? '')} ${String(payload.offered_resource_code ?? 'resources')} for ${String(payload.requested_amount ?? '')} ${String(payload.requested_resource_code ?? 'resources')}`;
  }
  if (eventType === 'civic_expedition_started') return 'A new civic expedition has begun';
  if (eventType === 'civic_expedition_completed')
    return 'Together, we restored the civic waterways';
  if (eventType === 'civic_expedition_contributed') {
    const role = { wayfinder: 'Wayfinder', scribe: 'Scribe', builder: 'Builder' }[
      String(payload.workstream)
    ];
    return role ? `${role} path restored` : 'Another expedition path restored';
  }
  return eventType.replaceAll('_', ' ');
}

const tribeProjectPhaseDescriptions: Readonly<Record<number, string>> = {
  1: 'Survey lines and the first civic terraces establish the shared footprint.',
  2: 'Stone crews raise the core while timber scaffolds define the emerging silhouette.',
  3: 'The civic core is standing. Crews now shape the plaza and first ornament layers.',
  4: 'Scaffolds recede as gardens, water stairs and woven civic details come alive.',
  5: 'The opened civic complex is inhabited, planted and active across the water.',
};

export function TribeExperience({ snapshot }: { snapshot: PlayerVerticalSliceView }) {
  const tribe = snapshot.tribe.current;
  if (tribe && snapshot.source !== 'demo_fixture') {
    return (
      <CivicExpeditionProvider
        enabled={snapshot.featureFlags.tribesEnabled}
        key={`${snapshot.profile.userId}:${tribe.id}:${tribe.currentUserRole}`}
        tribeId={tribe.id}
      >
        <TribeExperienceContent snapshot={snapshot} />
      </CivicExpeditionProvider>
    );
  }
  return <TribeExperienceContent snapshot={snapshot} />;
}

function TribeExperienceContent({ snapshot }: { snapshot: PlayerVerticalSliceView }) {
  const online = useNetworkStatus();
  const journey = useJourneySafety();
  const command = useVerticalSliceCommand();
  const [tribeName, setTribeName] = useState('People of the Jade Water');
  const current = snapshot.tribe.current;
  const project = current?.project;
  const [resourceCode, setResourceCode] = useState<ResourceCode>(
    project?.requirements[0]?.resourceCode ?? 'maize',
  );
  const [contribution, setContribution] = useState(10);
  const visualFixture = visualFixtureName();
  const phaseFixture = visualFixture?.match(/^tribe-phase-([1-5])$/);
  const visualState =
    visualFixture === 'overview'
      ? 'tribe-overview'
      : visualFixture === 'project'
        ? 'tribe-project'
        : phaseFixture
          ? `tribe-phase-${phaseFixture[1]}`
          : undefined;
  useVisualReady(visualState, true);
  const safetyLocked = journey.safety.mode === 'journey-passive';
  const mutationLocked = !online || safetyLocked || command.isPending;
  const canManageRoles = current?.currentUserRole === 'leader';
  const projectProgress = Math.round((project?.progress ?? 0) * 100);
  const projectVisual = resolveTribeProjectVisualForPhase(
    project?.visualStageAssetKey,
    project?.phase,
  );
  const projectLayer = resolveTribeProjectLayer(projectVisual.projectCode, projectVisual.phase);
  const projectVisualStyle = {
    '--tribe-project-progress': `${projectProgress}%`,
    '--tribe-foundation-opacity': projectVisual.layers.foundation,
    '--tribe-structure-scale': 0.58 + projectVisual.layers.structure * 0.42,
    '--tribe-scaffold-opacity': projectVisual.layers.scaffold,
    '--tribe-ornament-opacity': projectVisual.layers.ornament,
    '--tribe-water-opacity': projectVisual.layers.water,
    '--tribe-garden-opacity': projectVisual.layers.garden,
    '--tribe-population-opacity': projectVisual.layers.population,
    '--tribe-activation-opacity': 0.12 + projectVisual.layers.activation * 0.72,
    '--tribe-project-art-image': projectLayer ? `url("${projectLayer.url}")` : 'none',
  } as CSSProperties;

  if (!current) {
    return (
      <section
        className="player-screen tribe-discovery"
        data-visual-ready={visualState ? 'true' : undefined}
        data-visual-state={visualState}
      >
        <header>
          <span className="realm-screen__eyebrow">Belong · optional path</span>
          <h1>Civic projects awaken with a shared horizon</h1>
          <p>
            Join a civic project for spectacle and prestige. The complete solo path remains open.
          </p>
        </header>
        <section aria-labelledby="available-tribes-title" className="tribe-directory">
          <h2 id="available-tribes-title">Nearby Tribes</h2>
          <div>
            {snapshot.tribe.discovered.map((tribe) => (
              <StonePanel className="tribe-directory-card" key={tribe.id}>
                <span className="realm-screen__eyebrow">{tribe.rankTier}</span>
                <h3>{tribe.name}</h3>
                <p>
                  {tribe.memberCount} members · {tribe.reputation} reputation
                </p>
                <PrimaryActionButton
                  disabled={mutationLocked || !snapshot.featureFlags.tribesEnabled}
                  loading={command.isPending}
                  onClick={() =>
                    command.mutate({
                      type: 'tribe.join',
                      commandId: commandId(`tribe.join.${tribe.id}`),
                      tribeId: tribe.id,
                    })
                  }
                >
                  Join Tribe
                </PrimaryActionButton>
              </StonePanel>
            ))}
            {snapshot.tribe.discovered.length === 0 && (
              <StatePanel kind="empty" title="No open Tribes nearby">
                Found a new one below, or continue the complete solo path.
              </StatePanel>
            )}
          </div>
        </section>
        <ParchmentPanel className="tribe-found-panel">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              command.mutate({
                type: 'tribe.create',
                commandId: commandId('tribe.create'),
                name: tribeName.trim(),
              });
            }}
          >
            <label htmlFor="tribe-name">Found a Tribe</label>
            <input
              id="tribe-name"
              maxLength={48}
              minLength={3}
              onChange={(event) => setTribeName(event.target.value)}
              required
              value={tribeName}
            />
            <PrimaryActionButton
              disabled={mutationLocked || !snapshot.featureFlags.tribesEnabled}
              loading={command.isPending}
              type="submit"
            >
              Found shared city
            </PrimaryActionButton>
          </form>
        </ParchmentPanel>
        {!snapshot.featureFlags.tribesEnabled && (
          <p className="inline-error" role="status">
            Tribe creation is disabled in this realm.
          </p>
        )}
        {command.error && (
          <p className="inline-error" role="alert">
            {command.error.message}
          </p>
        )}
      </section>
    );
  }

  return (
    <section
      className="player-screen tribe-city-screen"
      data-current-role={current.currentUserRole}
      data-project-stage={project?.phase}
      data-project-visual-key={projectVisual.key}
      data-project-visual-source={projectVisual.productionGeometry}
      data-visual-ready={visualState ? 'true' : undefined}
      data-visual-state={visualState}
    >
      <header className="tribe-city-heading">
        <div>
          <span className="realm-screen__eyebrow">Belong · shared city</span>
          <h1>{current.name}</h1>
          <p>
            {current.memberCount} {current.memberCount === 1 ? 'member' : 'members'} ·{' '}
            {current.rankTier} · {current.reputation} reputation
          </p>
        </div>
        <span className="tribe-role-seal">Your role · {current.currentUserRole ?? 'visitor'}</span>
      </header>

      <section className="tribe-city-hero" aria-labelledby="civic-project-title">
        <div
          aria-hidden="true"
          className="tribe-city-hero__world"
          data-project-palette={projectVisual.palette}
          data-project-raster={projectLayer ? 'true' : undefined}
          data-project-runtime-stage={projectVisual.stage}
          data-project-visual-key={projectVisual.key}
          style={projectVisualStyle}
        >
          <svg className="tribe-project-filter-definitions" focusable="false">
            <defs>
              <filter
                colorInterpolationFilters="sRGB"
                height="140%"
                id="tribe-project-luma-key"
                width="140%"
                x="-20%"
                y="-20%"
              >
                <feColorMatrix
                  type="matrix"
                  values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0.2126 0.7152 0.0722 0 -0.078"
                />
                <feComponentTransfer>
                  <feFuncA intercept="0" slope="5" type="linear" />
                </feComponentTransfer>
              </filter>
            </defs>
          </svg>
          <span className="tribe-city-hero__mist" />
          <span className="tribe-city-hero__foundation" />
          <span className="tribe-city-hero__waterworks" />
          <span className="tribe-city-hero__project-contact" />
          <span className="tribe-city-hero__project-art" />
          <span className="tribe-city-hero__scaffold" />
          <span className="tribe-city-hero__gardens" />
          <span className="tribe-city-hero__project-light" />
          <span className="tribe-city-hero__boat tribe-city-hero__boat--one" />
          <span className="tribe-city-hero__boat tribe-city-hero__boat--two" />
          <span className="tribe-city-hero__population tribe-city-hero__population--one" />
          <span className="tribe-city-hero__population tribe-city-hero__population--two" />
          <CivicExpeditionScene passive={safetyLocked} />
          <ol className="tribe-city-hero__phase-story">
            {['Terraces', 'Core', 'Plaza', 'Gardens', 'Consecration'].map((phase, index) => (
              <li
                data-complete={index + 1 < (project?.phase ?? 0) || undefined}
                data-current={index + 1 === project?.phase || undefined}
                key={phase}
              >
                <span>{index + 1}</span>
                {phase}
              </li>
            ))}
          </ol>
        </div>
        {project ? (
          <StonePanel className="tribe-project-card" ornament={2}>
            <span className="realm-screen__eyebrow">
              Great project · phase {project.phase} of {project.phaseCount}
            </span>
            <h2 id="civic-project-title">{project.title}</h2>
            <ProgressMeter
              label={`${project.title} phase progress`}
              value={project.progress * 100}
            />
            <form
              className="tribe-contribution-form"
              onSubmit={(event) => {
                event.preventDefault();
                command.mutate({
                  type: 'tribe.contribute',
                  commandId: commandId(`tribe.contribute.${project.id}.${resourceCode}`),
                  tribeId: current.id,
                  projectId: project.id,
                  resourceCode,
                  amount: contribution,
                });
              }}
            >
              <label htmlFor="tribe-resource">Contribution</label>
              <select
                id="tribe-resource"
                onChange={(event) => setResourceCode(event.target.value as ResourceCode)}
                value={resourceCode}
              >
                {project.requirements.map((requirement) => (
                  <option key={requirement.resourceCode} value={requirement.resourceCode}>
                    {requirement.label}
                  </option>
                ))}
              </select>
              <label className="ar-visually-hidden" htmlFor="tribe-contribution-amount">
                Amount
              </label>
              <input
                id="tribe-contribution-amount"
                max={100000}
                min={1}
                onChange={(event) => setContribution(event.target.valueAsNumber)}
                type="number"
                value={contribution}
              />
              <PrimaryActionButton
                aria-label="Contribute to project"
                disabled={mutationLocked}
                loading={command.isPending}
                type="submit"
              >
                <span className="tribe-contribution-label tribe-contribution-label--full">
                  Contribute to project
                </span>
                <span
                  aria-hidden="true"
                  className="tribe-contribution-label tribe-contribution-label--compact"
                >
                  Contribute
                </span>
              </PrimaryActionButton>
            </form>
            <p className="tribe-project-card__impact">
              <strong>{projectProgress}% physically restored</strong>
              {tribeProjectPhaseDescriptions[project.phase] ??
                'The shared city reflects the current authoritative project phase.'}
            </p>
            <ul className="tribe-project-requirements">
              {project.requirements.map((requirement) => (
                <li key={requirement.resourceCode}>
                  <span>{requirement.label}</span>
                  <strong>
                    {requirement.current} / {requirement.target}
                  </strong>
                </li>
              ))}
            </ul>
          </StonePanel>
        ) : (
          <StatePanel kind="empty" title="The next civic project is being prepared">
            The shared city remains visible while a new project opens.
          </StatePanel>
        )}
      </section>

      <CivicExpeditionCityStatus passive={safetyLocked} />

      {snapshot.source !== 'demo_fixture' && (
        <TribeAssistancePanel
          enabled={snapshot.featureFlags.tribesEnabled}
          locked={mutationLocked}
          safetyLocked={safetyLocked}
          tribeId={current.id}
          onPlay={(request) => {
            if (request.target_kind === 'project' && project?.id === request.target_id) {
              if (project.requirements.some((item) => item.resourceCode === request.category)) {
                setResourceCode(request.category as ResourceCode);
              }
              const control = document.getElementById('tribe-resource');
              control?.scrollIntoView({ block: 'center' });
              control?.focus();
            } else if (request.target_kind === 'expedition') {
              const heading = document.getElementById('civic-expedition-title');
              heading?.scrollIntoView({ block: 'center' });
              heading?.focus();
            }
          }}
        />
      )}

      {snapshot.source !== 'demo_fixture' && (
        <CivicExpeditionPanel
          locked={mutationLocked}
          safetyLocked={safetyLocked}
          tribeId={current.id}
        />
      )}

      {snapshot.source !== 'demo_fixture' && (
        <TribeExchangePanel
          locked={mutationLocked}
          safetyLocked={safetyLocked}
          tribeId={current.id}
        />
      )}

      <section aria-labelledby="tribe-feed-title" className="tribe-feed">
        <h2 id="tribe-feed-title">City chronicle</h2>
        <ol>
          {snapshot.tribe.feed.map((event) => (
            <li key={event.id}>
              <span aria-hidden="true">✦</span>
              <div>
                <strong>{feedLabel(event.eventType, event.payload)}</strong>
                <small>{new Date(event.createdAt).toLocaleString()}</small>
              </div>
            </li>
          ))}
          {snapshot.tribe.feed.length === 0 && <li>No civic events yet.</li>}
        </ol>
      </section>

      <section aria-labelledby="tribe-leaderboard-title" className="tribe-leaderboard">
        <header>
          <h2 id="tribe-leaderboard-title">Contribution board</h2>
          <small>{snapshot.tribe.leaderboardPeriod ?? 'Current period'}</small>
        </header>
        <ol>
          {snapshot.tribe.leaderboard.map((entry) => (
            <li key={entry.entityId}>
              <span>{entry.rank}</span>
              <strong>{entry.displayName}</strong>
              <span>{entry.score}</span>
            </li>
          ))}
        </ol>
      </section>

      <section aria-labelledby="tribe-members-title" className="tribe-members">
        <h2 id="tribe-members-title">Members</h2>
        <ul>
          {current.members.map((member) => (
            <li key={member.memberId}>
              <div>
                <strong>{member.displayName}</strong>
                <small>{member.contributionScore} contribution</small>
              </div>
              {canManageRoles && member.memberId !== current.leaderMemberId ? (
                <label>
                  <span className="ar-visually-hidden">Role for {member.displayName}</span>
                  <select
                    disabled={mutationLocked}
                    onChange={(event) =>
                      command.mutate({
                        type: 'tribe.member.role',
                        commandId: commandId(`tribe.role.${member.memberId}`),
                        tribeId: current.id,
                        memberId: member.memberId,
                        role: event.target.value as TribeRole,
                      })
                    }
                    value={member.role}
                  >
                    {(['member', 'veteran', 'officer', 'leader'] as const).map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <span>{member.role}</span>
              )}
            </li>
          ))}
        </ul>
        <SecondaryActionButton
          disabled={mutationLocked || current.currentUserRole === 'leader'}
          onClick={() =>
            command.mutate({
              type: 'tribe.leave',
              commandId: commandId(`tribe.leave.${current.id}`),
              tribeId: current.id,
            })
          }
        >
          Leave Tribe
        </SecondaryActionButton>
        {current.currentUserRole === 'leader' && (
          <small>Transfer leadership before leaving the Tribe.</small>
        )}
      </section>
      {command.error && (
        <p className="inline-error tribe-error" role="alert">
          {command.error.message}
        </p>
      )}
    </section>
  );
}

const previewRewards: readonly ResourceDisplay[] = [
  { code: 'maize', label: 'Maize', amount: 400 },
  { code: 'jade', label: 'Jade', amount: 12 },
  { code: 'knowledge', label: 'Knowledge', amount: 1 },
];

export function CompactMissionPanel({ snapshot }: { snapshot?: PlayerVerticalSliceView }) {
  if (!snapshot) return <LoadingState title="Preparing today’s path" />;
  const mission = snapshot.settlement.activeMission;
  return (
    <StonePanel
      className="shell-context-card"
      data-testid={mission.progress >= mission.target ? 'scenario-a-mission-complete' : undefined}
      ornament={2}
    >
      <span className="realm-screen__eyebrow">Today’s path</span>
      <h2>{mission.title}</h2>
      <p>{mission.description}</p>
      <ProgressMeter label="Mission progress" max={mission.target} value={mission.progress} />
      <ol className="mission-steps">
        {mission.steps.map((step) => (
          <li data-complete={step.complete || undefined} key={step.id}>
            <span aria-hidden="true">{step.complete ? '✓' : '◇'}</span>
            {step.label}
          </li>
        ))}
      </ol>
      <small>Reward · {mission.rewardLabel}</small>
    </StonePanel>
  );
}

export { previewRewards };
